import PageMetaHiddenField from "@/commonComponent/PageMetaHiddenField";
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  FormControlLabel,
  Button,
  Tabs,
  Tab,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  Radio,
  TextField,
  RadioGroup,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress
} from '@mui/material';
import Chip from '@mui/material/Chip';
import {
  DataGridPro,
  GridToolbar,
  GridOverlay,
  useGridApiRef,
  getGridDefaultColumnTypes,
  gridStringOrNumberComparator
} from '@mui/x-data-grid-pro';
import callApi from 'hooks/useApi';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { IconSettings, IconDownload, IconAdjustmentsHorizontal, IconReload } from '@tabler/icons-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme, alpha } from '@mui/material/styles';
import { useIntl, FormattedMessage } from 'react-intl';
import emptyTableLightImage from 'assets/images/emptyTableLightImage.png';
import emptyTableDarkImage from 'assets/images/emptyTableDarkImage.png';
import useConfig from '../../hooks/useConfig';
import CommonDrawer from '../../commonComponent/drawerComponent/CommonDrawer';
import DrawerFooter from '../../commonComponent/drawerComponent/DrawerFooter';
import DrawerHeader from '../../commonComponent/drawerComponent/DrawerHeader';

import DrawerContainer from '../../commonComponent/drawerComponent/DrawerContainer';
import { buildCsvExportFileName } from '../../utils/csvExportFileName';

/**
 * SmartDataGrid is a reusable component for rendering paginated, searchable, and sortable data tables.
 * Supports both client-side and server-side pagination based on page size.
 *
 * Column localization: set optional `headerNameIntlId` on a column definition (same id as renderHeader
 * FormattedMessage) so the column header, filter, settings, and filter chips use the translated label.
 */
const SmartDataGrid = ({
  apiConfig = {},
  columns,
  onRowSelectionModelChange,
  rowSelectionModel,
  onPaginationModelChange,
  children,
  onDataChange,
  onRowsChange,
  onLoadingChange,
  callingFrom,
  queryParams,
  initialFilters,
  rightHeaderChildren,
  afterSettingsChildren,
  onRowClick,
  onRowDoubleClick,
  operationSide = 'server',
  isRowSelectable,
  showFilterChips = true,
  filterRequired = 'Yes',
  settingsRequired = 'Yes',
  showCheckbox = true,
  minSearchLength = 3,
  userPreferenceRequired = 'Yes',
  rowHeight,
  getRowHeight,
  customFilterHandlers,
  getRowClassName,
  clientData,
  showSearchBox = true,
  /** Optional placeholder for the built-in search box; defaults to "Search...". */
  searchPlaceholder,
  disableRowSelectionExcludeModel,
  keepNonExistentRowsSelected,
  showRowActionSettings = true,
  hidePagination = false,
  /**
   * When using `clientData` with `hidePagination`, the grid still paginates internally by default
   * (page size 25). Set to true so pager size matches row count and all rows render without the footer.
   */
  fitClientPageToHiddenPagination = false,
  /** When true, only first / previous / next page moves are allowed (matches cursor-based list APIs). */
  sequentialPaginationOnly = false,
  refreshRequired = 'Yes',
  /** When set, empty grid uses intl.formatMessage({ id: noRowsMessageId }). Ignored if `noRowsOverlayText` is provided. */
  noRowsMessageId,
  noRowsOverlayText,
  /** When false, hides the CSV export toolbar button (default true for e.g. device lists). */
  showDownloadButton = true,
  /** Optional custom download handler. When provided, overrides the default CSV export behavior. */
  onDownload,
  /** Optional mapper over the visible columns used by the default CSV export. */
  getExportColumns,
  /** Page title used for the default CSV download filename (e.g. "Schedules" → "Schedules.csv"). Falls back to the breadcrumb heading when omitted. */
  pageTitle,
  /** Pre-fills the grid search box and triggers an initial server/client search (e.g. global search deep-link). */
  initialSearchText = '',
  /** Notified when the effective server/client search text changes (including clear). */
  onSearchTextChange,
  getDetailPanelContent,
  getDetailPanelHeight,
  detailPanelExpandedRowIds,
  onDetailPanelExpandedRowIds,
  /**
   * Opt-in: render the detail-panel expand/collapse toggle as vertical chevrons (Figma) instead of
   * the MUI X default +/-. Off by default so existing detail-panel grids (e.g. NotificationAlarmsGrid)
   * keep their toggle unchanged; only the stacking Devices table enables it.
   */
  useChevronDetailPanelIcons = false,
  /** Optional parent-owned apiRef. When provided, the parent can call resetRowHeights etc. Falls back to internal ref. */
  gridApiRef: gridApiRefProp,
  /**
   * When true (client-side grids only), if the current page is past the last page of filtered
   * in-memory rows, pagination resets to page 0. Use when the parent drives filtering (e.g.
   * external search + customFilterHandlers.clientFilter) without the built-in search box.
   * Default false leaves SmartDataGrid behavior unchanged for existing callers.
   */
  resetPaginationOnFilterChange = false,
  /**
   * Opt-in, client-side grids only: sort the complete filtered rows by the sort model before they are paged.
   * By default the grid hands DataGridPro one page and DataGridPro sorts that page alone, so changing the sort
   * never moves a row to another page. Columns sort as DataGridPro sorts them: `getSortComparator`, else
   * `sortComparator`, else the column type's comparator, on the `valueGetter` value.
   */
  sortClientDataAcrossPages = false,
  /** Initial sort model to apply when the grid first loads (e.g. [{ field: 'name', sort: 'asc' }]). */
  initialSortModel = [],
  /**
   * Persist the applied sort and column filters in the user preference (as `userFilter`, beside the
   * column settings) and restore them before the first fetch. Applies to every grid with
   * `apiConfig.userPreferenceAPIKey`; pass false to opt a grid out. Filters passed through
   * `initialFilters` (e.g. a dashboard deep link) win over the saved ones.
   */
  persistSortAndFilter = true,
  /**
   * Optional refresh handler for client-side data. The refresh control already guards on
   * `typeof onRefresh === 'function'`, but the prop was never declared, so it was always
   * undefined and that branch could not run. No caller passes it today.
   */
  onRefresh,
  ...rest
}) => {
  const MAX_LEFT_PINNED_ITEMS = 4;
  const intl = useIntl();
  const getColumnDisplayLabel = useCallback(
    (col) => {
      if (!col) return '';
      const intlId =
        col.headerNameIntlId || (typeof col.headerName === 'string' && /^[a-z][a-zA-Z0-9]*$/.test(col.headerName) ? col.headerName : null);
      if (intlId) {
        return intl.formatMessage({ id: intlId, defaultMessage: col.headerName || col.field });
      }
      return col.headerName ?? col.field ?? '';
    },
    [intl]
  );
  const lastFetchParams = useRef({ key: '', sortModelKey: '', searchText: '' });
  // Empty table icons for light and dark themes
  const { mode } = useConfig();
  const emptyTableImage = mode === 'dark' ? emptyTableDarkImage : emptyTableLightImage;
  // API ref for DataGridPro to preserve scroll position during data accumulation
  const internalApiRef = useGridApiRef();
  const apiRef = gridApiRefProp ?? internalApiRef;
  // Ref to store scroll position before data accumulation
  const scrollPositionRef = useRef({ top: 0, left: 0 });
  // Default pager size - used for initial fetch before API response is available
  // If apiConfig.pagerSize is a number, use it; if function, use 200 as initial default
  const defaultPagerSize = typeof apiConfig.pagerSize === 'number' ? apiConfig.pagerSize : 200;
  const [pagerSize, setPagerSize] = useState(defaultPagerSize);
  const pageSizeOptions = [25, 50, 75, 100];
  const [fullData, setFullData] = useState([]);
  // Export is awaiting a column's csvPrepare — disables the download control.
  const [csvExporting, setCsvExporting] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [serverPage, setServerPage] = useState(0);
  // Track which server pages have been loaded to support data accumulation
  const [_loadedServerPages, setLoadedServerPages] = useState(new Set());
  // Ref to track loadedServerPages without causing effect re-runs
  const loadedServerPagesRef = useRef(new Set());
  // Track if user preferences have been fetched (prevents duplicate calls in client-side mode)
  const userPrefFetchedRef = useRef(false);
  const persistUserFilter = persistSortAndFilter && Boolean(apiConfig.userPreferenceAPIKey) && userPreferenceRequired === 'Yes';
  // The userFilter last read from or written to the preference. Every preference save carries it, so
  // saving the column settings does not drop the saved sort and filters.
  const savedUserFilterRef = useRef(null);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  // Holds the first fetch until the saved sort and filters are applied, so the grid does not load
  // unsorted and then reload.
  const [userFilterRestored, setUserFilterRestored] = useState(() => !persistUserFilter);
  // Track the base offset for accumulated data (first loaded server page * pagerSize)
  const [dataStartIndex, setDataStartIndex] = useState(0);
  const trimmedInitialSearchText = (initialSearchText || '').trim();
  const [searchText, setSearchText] = useState(() => trimmedInitialSearchText);
  const [searchInput, setSearchInput] = useState(() => trimmedInitialSearchText);
  const [clientFilterText, setClientFilterText] = useState('');

  // Ref for debounce timer to delay search API calls (500ms after user stops typing)
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    onSearchTextChange?.(searchText);
  }, [searchText, onSearchTextChange]);

  // Apply handoff when parent passes initialSearchText after lazy mount (useState only runs once)
  useEffect(() => {
    const trimmed = (initialSearchText || '').trim();
    if (!trimmed) return;
    setSearchInput(trimmed);
    setSearchText(trimmed);
    setOpenSearch(true);
  }, [initialSearchText]);

  // Cleanup debounce timer on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const [error, setError] = useState(null);
  // True when this instance will fetch on mount, so the empty-state overlay doesn't flash before loading.
  const [loading, setLoading] = useState(
    () => !(clientData && Array.isArray(clientData)) && (Boolean(apiConfig?.apiKey) || typeof apiConfig?.customFetch === 'function')
  );
  const [sortModel, setSortModel] = useState(initialSortModel);
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  // Reported through a ref so an inline arrow from the caller does not re-fire this every render.
  useEffect(() => {
    onLoadingChangeRef.current?.(loading);
  }, [loading]);
  /** Latest `initialSortModel` from props (refresh uses this so inline array literals are always current). */
  const initialSortModelRef = useRef(initialSortModel);
  initialSortModelRef.current = initialSortModel;
  const theme = useTheme();

  const resolvedNoRowsOverlayLabel = useMemo(() => {
    if (noRowsOverlayText !== undefined && noRowsOverlayText !== null) {
      return noRowsOverlayText;
    }
    if (noRowsMessageId) {
      return intl.formatMessage({ id: noRowsMessageId, defaultMessage: 'No results found' });
    }
    return intl.formatMessage({ id: 'noResultsFound', defaultMessage: 'No results found' });
  }, [noRowsOverlayText, noRowsMessageId, intl]);

  // Drawer states
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [pinnedColumns, setPinnedColumns] = useState(['__check__']);
  const [density, setDensity] = useState('standard');
  const [tabValue, setTabValue] = useState(0);
  const [rowAction, setRowAction] = useState('show');
  // Column order state for reordering
  const [columnOrder, setColumnOrder] = useState([]);
  // Backup states for settings drawer cancel functionality
  const [originalColumnVisibilityModel, setOriginalColumnVisibilityModel] = useState({});
  const [originalPinnedColumns, setOriginalPinnedColumns] = useState(['__check__']);
  const [originalDensity, setOriginalDensity] = useState('standard');
  const [originalRowAction, setOriginalRowAction] = useState('show');
  const [originalColumnOrder, setOriginalColumnOrder] = useState([]);
  const normalizeInitialFilters = (filtersArg) => {
    if (!filtersArg) return [];
    if (Array.isArray(filtersArg)) return filtersArg;
    return [filtersArg];
  };

  const normalizedInitialFilters = useMemo(() => normalizeInitialFilters(initialFilters), [initialFilters]);
  const initialFiltersSerializedRef = useRef(normalizedInitialFilters.length ? JSON.stringify(normalizedInitialFilters) : null);

  // Filter states
  const [filters, setFilters] = useState(() => normalizedInitialFilters);
  const [draftFilters, setDraftFilters] = useState(() => normalizedInitialFilters);
  const [userPrefSuccessOpen, setUserPrefSuccessOpen] = useState(false);
  const [pinLimitAlertOpen, setPinLimitAlertOpen] = useState(false);
  const { showLoader, hideLoader } = useConfig();
  // Selection count state
  const [_selectedRowCount, setSelectedRowCount] = useState(0);

  useLayoutEffect(() => {
    if (!fitClientPageToHiddenPagination || !hidePagination) return;
    if (!clientData || !Array.isArray(clientData)) return;
    const n = clientData.length;
    const size = Math.max(n, 1);
    setPagerSize(size);
    setPaginationModel({ page: 0, pageSize: size });
  }, [clientData, hidePagination, fitClientPageToHiddenPagination]);

  // Custom filter states for specific implementations
  const [customFilterState, setCustomFilterState] = useState({});
  const [customFilterData, setCustomFilterData] = useState({});

  const _pageSizeOptionNames = {
    25: 'twentyFive',
    50: 'fifty',
    75: 'seventyFive',
    100: 'oneHundred'
  };
  // Normalize columns to ensure all have renderCell defined, using a default that applies ellipsis and tooltip for overflow.
  // A declared headerNameIntlId also localizes the header the grid draws from headerName (English).
  // Left alone: a column with its own renderHeader (it draws the header, and its id may name a
  // different filter/settings label), and an empty headerName, which is a deliberately blank header.
  const normalizedColumns = useMemo(() => {
    return columns.map((column) => {
      const col =
        column.headerNameIntlId && column.headerName !== '' && !column.renderHeader
          ? { ...column, headerName: getColumnDisplayLabel(column) }
          : column;
      if (col.renderCell) return col;

      return {
        ...col,
        renderCell: (params) => (
          <Typography
            component="span"
            variant="body1"
            title=""
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {params.value ?? ''}
          </Typography>
        )
      };
    });
  }, [columns, getColumnDisplayLabel]);
  // Compute ordered columns based on columnOrder state
  const orderedColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) {
      return normalizedColumns;
    }

    // Create a map for quick lookup
    const columnMap = {};
    normalizedColumns.forEach((col) => {
      columnMap[col.field] = col;
    });

    // Build ordered array: first add columns in order, then add any remaining columns not in order
    const ordered = [];
    columnOrder.forEach((field) => {
      if (columnMap[field]) {
        ordered.push(columnMap[field]);
        delete columnMap[field];
      }
    });

    // Add remaining columns (new columns not yet in saved order)
    Object.values(columnMap).forEach((col) => {
      ordered.push(col);
    });

    return ordered;
  }, [normalizedColumns, columnOrder]);

  // Explicit client operation must keep using the filtered client-row pipeline even when
  // hidden pagination fits the page size to the complete data set.
  const isClientSide = operationSide === 'client' || paginationModel.pageSize !== pagerSize;

  /**
   * Toggles the settings drawer open/close state.
   */
  const toggleSettingsDrawer = () => {
    setSettingsDrawerOpen((prev) => {
      if (!prev) {
        // Opening the drawer - save current state as backup
        setOriginalColumnVisibilityModel({ ...columnVisibilityModel });
        setOriginalPinnedColumns([...pinnedColumns]);
        setOriginalDensity(density);
        setOriginalColumnOrder([...columnOrder]);
        if (showRowActionSettings) {
          setOriginalRowAction(rowAction);
        }
      } else {
        // Closing the drawer - revert changes based on active tab
        if (tabValue === 0) {
          // Column Settings tab - revert column-related settings
          setColumnVisibilityModel({ ...originalColumnVisibilityModel });
          setPinnedColumns([...originalPinnedColumns]);
          setColumnOrder([...originalColumnOrder]);
        } else if (tabValue === 1) {
          // Row Settings tab - revert row-related settings
          setDensity(originalDensity);
          if (showRowActionSettings) {
            setRowAction(originalRowAction);
          }
        }
      }
      return !prev;
    });
  };

  /**
   * Resets BOTH Column Settings and Row Settings to factory defaults,
   * persists the defaults via saveUserPreference, and closes the drawer.
   * Used by the Reset button on both tabs so behavior is identical.
   */
  const handleResetSettings = () => {
    const defaultVisibility = {};
    columns.forEach((col) => {
      defaultVisibility[col.field] = col.hide === true ? false : true;
    });
    const defaultPinnedColumns = ['__check__'];
    const defaultColumnOrder = columns.map((col) => col.field).filter((field) => field !== 'actions');
    const defaultDensity = 'standard';
    const defaultRowAction = 'show';

    // Reset resized column widths
    setColumnWidths({});

    if (STORAGE_KEY) {
      localStorage.removeItem(STORAGE_KEY);
    }

    // Update temp state so the drawer briefly reflects defaults before close.
    setTempColumnVisibility(defaultVisibility);
    setTempPinnedColumns(defaultPinnedColumns);
    setTempColumnOrder(defaultColumnOrder);
    setTempDensity(defaultDensity);
    if (showRowActionSettings) {
      setTempRowAction(defaultRowAction);
    }

    // Persist defaults and close the drawer (saveUserPreference closes it).
    saveUserPreference({
      columnVisibility: defaultVisibility,
      pinnedCols: defaultPinnedColumns,
      rowDensityValue: defaultDensity,
      rowActionValue: defaultRowAction,
      colOrder: defaultColumnOrder
    });
  };

  /**
   * Toggles the filter drawer open/close state.
   */
  const isFilterApplied = filters.some((f) => f.field && f.value);

  const toggleFilterDrawer = (open) => {
    const nextOpen = typeof open === 'boolean' ? open : !filterDrawerOpen;
    if (nextOpen) {
      const firstCol = columns[0] || {};
      // Keep edits in drawer-only state; applied filters update only on Apply
      const initialDraftFilters =
        filters.length > 0
          ? filters
          : [
              {
                field: firstCol?.filterColumnName,
                operator: '=',
                value: getFirstOptionValue(firstCol?.filtersValue),
                filtersValue: firstCol?.filtersValue
              }
            ];
      setDraftFilters(initialDraftFilters.map((item) => ({ ...item })));
      // Load custom filters if handlers are provided
      if (customFilterHandlers?.loadFilters) {
        customFilterHandlers.loadFilters();
      }
    } else {
      setDraftFilters(filters.map((item) => ({ ...item })));
    }
    setFilterDrawerOpen(nextOpen);
  };
  /**
   * Toggles column visibility and row action state.
   * @param {string} field - Column field name
   * @param {any} value - Value for row action (optional)
   */
  const _handleToggleColumn = (field, value) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: !prev[field] // reliably toggles between true and false
    }));
    field == 'actions' && setRowAction(value);
  };

  /**
   * Toggles pinning of a column.
   * @param {string} field - Column field name
   */
  const _handleTogglePin = (field) => {
    const pinnedUserColumns = pinnedColumns.filter((key) => key !== '__check__');
    const canAddMorePinned = pinnedUserColumns.length < MAX_LEFT_PINNED_ITEMS;
    if (!pinnedColumns.includes(field) && !canAddMorePinned) {
      setPinLimitAlertOpen(true);
      return;
    }
    const newPinnedColumns = pinnedColumns.includes(field)
      ? pinnedColumns.filter((key) => key !== field) // remove
      : canAddMorePinned
        ? [...pinnedColumns, field]
        : pinnedColumns;
    setPinnedColumns(newPinnedColumns);
  };

  /**
   * Handles column order change when user drags columns to reorder.
   * Automatically saves the new order to user preferences.
   * @param {object} params - Contains column, oldIndex, targetIndex
   * @param {object} event - The event object
   * @param {object} details - Contains api reference to get column state
   */
  const handleColumnOrderChange = async (params, event, details) => {
    // Get the actual column order from the DataGrid's API after the reorder
    // This gives us the correct visual order including all adjustments
    const allColumns = details.api.getAllColumns();

    // Extract field names in their new order, excluding special columns
    const newColumnOrder = allColumns.map((col) => col.field).filter((field) => field !== '__check__' && field !== 'actions');

    setColumnOrder(newColumnOrder);

    // Skip saving if userPreferenceAPIKey is not defined
    if (!apiConfig.userPreferenceAPIKey || userPreferenceRequired !== 'Yes') {
      return;
    }

    // Save the new column order to user preferences immediately
    try {
      // Build showColumn with visible columns in new order
      const showColumnOrdered = newColumnOrder.filter((key) => columnVisibilityModel[key] && key !== 'actions');

      const transformed = {
        showColumn: showColumnOrdered,
        leftPin: pinnedColumns.filter((key) => columnVisibilityModel[key] && key !== 'actions'),
        rowDensity: density,
        ...(showRowActionSettings && { inRowAction: rowAction }),
        ...(persistUserFilter && savedUserFilterRef.current && { userFilter: savedUserFilterRef.current })
      };

      await callApi(
        callingFrom === 'parOrg'
          ? 'SAVE_USER_PREFERENCE_PAR_ORG'
          : callingFrom === 'locationLevel'
            ? 'SAVE_USER_PREFERENCE_LOCATION_LEVEL'
            : 'SAVE_USER_PREFERENCE',
        {
          tableView: apiConfig.userPreferenceAPIKey,
          ...((callingFrom === 'parOrg' || callingFrom === 'locationLevel') && { orgId: localStorage.getItem('OrgId') || '' }),
          ...(callingFrom === 'locationLevel' && { networkId: JSON.parse(localStorage.getItem('selectedType')).id })
        },
        { data: transformed },
        apiConfig.getHeaders?.() || []
      );

      // Update backup states to reflect saved order
      setOriginalColumnOrder([...newColumnOrder]);
    } catch (error) {
      console.error('Error saving column order preference:', error);
    }
  };

  /**
   * Handles tab change in settings drawer.
   * @param {object} event - Event object
   * @param {number} newValue - New tab index
   */
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Get first selectable value for a column's filtersValue
  const getFirstOptionValue = (filtersValue) => {
    if (!Array.isArray(filtersValue) || filtersValue.length === 0) return '';
    const first = filtersValue[0];
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const keys = Object.keys(first);
      return keys.length > 0 ? keys[0] : '';
    }
    return first;
  };

  // Map stored filter value (e.g. "1") to its display label (e.g. "Troubleshooting")
  const getFilterDisplayValue = (filtersValue, rawValue) => {
    if (rawValue === undefined || rawValue === null || rawValue === '') return rawValue;
    if (!Array.isArray(filtersValue) || filtersValue.length === 0) return rawValue;

    const isDictArray =
      filtersValue.length === 1 && filtersValue[0] && typeof filtersValue[0] === 'object' && !Array.isArray(filtersValue[0]);

    if (isDictArray) {
      const dict = filtersValue[0];
      const key = String(rawValue);
      return Object.prototype.hasOwnProperty.call(dict, key) ? String(dict[key]) : rawValue;
    }

    const match = filtersValue.find((item) => String(item) === String(rawValue));
    return match !== undefined ? String(match) : rawValue;
  };

  // The preference keeps only what the list APIs take; a filter row also carries the UI-only `filtersValue`.
  const toUserFilter = (sort, appliedFilters) => ({
    ...(sort?.[0]?.field && sort[0].sort && { sortBy: sort[0].field, sortDir: sort[0].sort }),
    dataFilter: (appliedFilters || [])
      .filter((f) => f.field && f.value !== undefined && f.value !== null && f.value !== '')
      .map(({ field, operator, value }) => ({ field, operator, value }))
  });

  /**
   * Applies a saved userFilter. Sort and filter columns the grid no longer has are dropped.
   */
  const restoreUserFilter = (userFilter) => {
    savedUserFilterRef.current = userFilter;
    const { sortBy, sortDir, dataFilter } = userFilter;
    // Read the columns at restore time: the preference call resolves after the mount render, and some
    // pages only build their columns after it.
    const currentColumns = columnsRef.current || [];
    const sortColumn = currentColumns.find((col) => col.field === sortBy);
    if (sortColumn && sortColumn.sortable !== false && (sortDir === 'asc' || sortDir === 'desc')) {
      setSortModel([{ field: sortBy, sort: sortDir }]);
    }
    if (normalizedInitialFilters.length > 0 || !Array.isArray(dataFilter)) return;
    const restoredFilters = dataFilter.flatMap((f) => {
      const column = currentColumns.find((col) => (col.filterColumnName ?? col.field) === f.field);
      return column ? [{ field: f.field, operator: f.operator || '=', value: f.value, filtersValue: column.filtersValue }] : [];
    });
    setFilters(restoredFilters);
    setDraftFilters(restoredFilters);
  };

  /**
   * Saves the applied sort and filters together with the current column settings. Silent like the
   * column-order save: the grid already shows the change, so a failure only loses the persistence.
   */
  const saveUserFilter = async (nextSortModel, nextFilters) => {
    if (!persistUserFilter) return;
    const userFilter = toUserFilter(nextSortModel, nextFilters);
    savedUserFilterRef.current = userFilter;
    const transformed = {
      showColumn: orderedColumns.map((col) => col.field).filter((key) => columnVisibilityModel[key] && key !== 'actions'),
      leftPin: pinnedColumns.filter((key) => columnVisibilityModel[key] && key !== 'actions'),
      rowDensity: density,
      ...(showRowActionSettings && { inRowAction: rowAction }),
      userFilter
    };
    try {
      await callApi(
        callingFrom === 'parOrg'
          ? 'SAVE_USER_PREFERENCE_PAR_ORG'
          : callingFrom === 'locationLevel'
            ? 'SAVE_USER_PREFERENCE_LOCATION_LEVEL'
            : 'SAVE_USER_PREFERENCE',
        {
          tableView: apiConfig.userPreferenceAPIKey,
          ...((callingFrom === 'parOrg' || callingFrom === 'locationLevel') && { orgId: localStorage.getItem('OrgId') || '' }),
          ...(callingFrom === 'locationLevel' && { networkId: JSON.parse(localStorage.getItem('selectedType')).id })
        },
        { data: transformed },
        apiConfig.getHeaders?.() || []
      );
    } catch (error) {
      console.error('Error saving sort and filter preference:', error);
    }
  };

  // Ensure all columns are visible by default when no user preference is applied; respect col.hide (e.g. orgHealth)
  useEffect(() => {
    if (Object.keys(columnVisibilityModel).length === 0 && Array.isArray(columns) && columns.length > 0) {
      const initialModel = {};
      columns.forEach((col) => {
        initialModel[col.field] = col.hide === true ? false : true;
      });
      setColumnVisibilityModel(initialModel);
    }
    // seeds the initial model for a column set; adding columnVisibilityModel would re-run this on the state it sets
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // Sync selection count with rowSelectionModel prop changes
  useEffect(() => {
    if (!showCheckbox) {
      setSelectedRowCount(0);
      return;
    }

    // Only sync selection count when rowSelectionModel is controlled (provided as a prop)
    if (rowSelectionModel !== undefined) {
      if (rowSelectionModel && typeof rowSelectionModel === 'object') {
        if (rowSelectionModel.type === 'include' && rowSelectionModel.ids) {
          const count = Array.isArray(rowSelectionModel.ids)
            ? rowSelectionModel.ids.length
            : rowSelectionModel.ids instanceof Set
              ? rowSelectionModel.ids.size
              : 0;
          setSelectedRowCount(count);
        } else if (rowSelectionModel.type === 'exclude' && rowSelectionModel.ids) {
          // Clamp to 0 to prevent negative counts when excludedCount > totalRowCount
          const excludedCount = Array.isArray(rowSelectionModel.ids)
            ? rowSelectionModel.ids.length
            : rowSelectionModel.ids instanceof Set
              ? rowSelectionModel.ids.size
              : 0;
          setSelectedRowCount(Math.max(0, totalRowCount - excludedCount));
        } else if (Array.isArray(rowSelectionModel)) {
          setSelectedRowCount(rowSelectionModel.length);
        }
      } else if (Array.isArray(rowSelectionModel)) {
        setSelectedRowCount(rowSelectionModel.length);
      } else {
        setSelectedRowCount(0);
      }
    }
    // In uncontrolled mode (rowSelectionModel === undefined), don't reset the count here.
    // The count is managed by onRowSelectionModelChange callback, which DataGridPro calls
    // with the current selection state, ensuring the count persists across data changes.
  }, [rowSelectionModel, totalRowCount, showCheckbox]);

  /**
   * Fetches data from API using configuration and sort/filter info.
   * @param {number} serverPageToFetch - Page number to fetch
   * @param {string} search - Search text
   * @param {array} sortModel - Sort model array
   * @returns {object} - Transformed data, total row count, and raw data for dynamic config
   */
  const fetchDetails = useCallback(
    async (serverPageToFetch, search = '', sortModel = [], filters = [], queryParamsArg = {}) => {
      try {
        if (userPreferenceRequired === 'Yes' && !userPrefFetchedRef.current) {
          userPrefFetchedRef.current = true;
          fetchUserPreference();
        }
        // Opt-in override for callers that supply their own fetch (e.g. Connected Clients All tab).
        if (typeof apiConfig.customFetch === 'function') {
          return apiConfig.customFetch(serverPageToFetch, search, sortModel, filters, queryParamsArg);
        }
        // If no API key, return empty data (client-side only mode)
        if (!apiConfig.apiKey) {
          return {
            transformedData: [],
            totalRowCount: 0,
            rawData: null
          };
        }
        const params = apiConfig.getParams(serverPageToFetch, pagerSize, search, sortModel, filters, queryParamsArg);
        const body = apiConfig.getBody?.(sortModel, filters, search);
        const headers = apiConfig.getHeaders();
        const data = await callApi(apiConfig.apiKey, params, body, headers);
        // `filters` is passed through as a second argument so a config whose transform/count logic
        // depends on the active filter (e.g. the stack-grouped device configs) can derive it from the
        // filters that produced THIS response, instead of a shared mutable ref that a later, overlapping
        // fetch could have already overwritten by the time this one resolves. Configs that don't need it
        // simply ignore the extra argument.
        return {
          transformedData: apiConfig.transformResponse(data, filters),
          totalRowCount: apiConfig.getTotalCount(data, filters),
          rawData: data // Return raw data for dynamic config extraction (e.g., pagerSize function)
        };
      } catch (err) {
        throw err;
      }
    },
    // fetchUserPreference is redefined every render, so including it would rebuild this callback each time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiConfig, userPreferenceRequired, pagerSize]
  );

  /**
   * Wrapper to fetch and update component state.
   * Supports accumulating data across server pages for seamless client-side pagination.
   * @param {number} serverPageToFetch - Page number to fetch
   * @param {string} search - Search text
   * @param {array} sortModel - Sort model array
   * @param {array} filters - Filter array
   * @param {object} queryParamsArg - Additional query parameters
   * @param {boolean} shouldAccumulate - Whether to append data to existing (for spanning pages)
   * @param {boolean} resetGridScroll - If true, scroll grid to top-left after replace (search/sort/filter reset only; not server page changes)
   */
  const fetchDataWrapper = useCallback(
    async (
      serverPageToFetch,
      search = '',
      sortModel = [],
      filters = [],
      queryParamsArg = {},
      shouldAccumulate = false,
      resetGridScroll = false
    ) => {
      // Save scroll position before fetching when accumulating data
      // This prevents scroll jump when new data is appended/prepended
      if (shouldAccumulate && apiRef.current) {
        try {
          const scrollElement =
            apiRef.current.getScrollPosition?.() || apiRef.current.rootElementRef?.current?.querySelector('.MuiDataGrid-virtualScroller');
          if (scrollElement) {
            if (typeof scrollElement.scrollTop === 'number') {
              scrollPositionRef.current = { top: scrollElement.scrollTop, left: scrollElement.scrollLeft || 0 };
            } else {
              scrollPositionRef.current = scrollElement;
            }
          }
        } catch (e) {
          // Silently fail if scroll position can't be captured
        }
      }

      setLoading(true);
      try {
        const data = await fetchDetails(serverPageToFetch, search, sortModel, filters, queryParamsArg);

        // Update pagerSize dynamically if pagerSize is a function (extracts from API response)
        let effectivePagerSize = pagerSize;
        if (typeof apiConfig.pagerSize === 'function' && data.rawData) {
          const dynamicPagerSize = apiConfig.pagerSize(data.rawData);
          if (dynamicPagerSize && dynamicPagerSize !== pagerSize) {
            setPagerSize(dynamicPagerSize);
            effectivePagerSize = dynamicPagerSize;
          }
        }

        // Use ref to check loaded pages to avoid stale closure issues
        const currentLoadedPages = loadedServerPagesRef.current;

        if (shouldAccumulate && currentLoadedPages.size > 0) {
          // Accumulate data: append or prepend based on server page position
          setFullData((prevData) => {
            const newStartIndex = serverPageToFetch * effectivePagerSize;
            const currentStartIndex = dataStartIndex;

            if (newStartIndex >= currentStartIndex + prevData.length) {
              // New data comes after existing data - append
              return [...prevData, ...data.transformedData];
            } else if (newStartIndex + data.transformedData.length <= currentStartIndex) {
              // New data comes before existing data - prepend and update start index
              setDataStartIndex(newStartIndex);
              return [...data.transformedData, ...prevData];
            } else {
              // Overlapping or same range - replace
              return data.transformedData;
            }
          });
          const newLoadedPages = new Set([...currentLoadedPages, serverPageToFetch]);
          loadedServerPagesRef.current = newLoadedPages;
          setLoadedServerPages(newLoadedPages);

          // Restore vertical scroll after accumulation; do not restore scrollLeft — restoring
          // horizontal position after row height/layout settles causes visible scrollbar "walking".
          requestAnimationFrame(() => {
            if (apiRef.current && scrollPositionRef.current) {
              try {
                const scrollElement = apiRef.current.rootElementRef?.current?.querySelector('.MuiDataGrid-virtualScroller');
                if (scrollElement) {
                  scrollElement.scrollTop = scrollPositionRef.current.top;
                }
              } catch (e) {
                // Silently fail if scroll position can't be restored
              }
            }
          });
        } else {
          // Fresh fetch - replace all data and reset tracking
          setFullData(data.transformedData);
          setDataStartIndex(serverPageToFetch * effectivePagerSize);
          const newLoadedPages = new Set([serverPageToFetch]);
          loadedServerPagesRef.current = newLoadedPages;
          setLoadedServerPages(newLoadedPages);
          // Notify apiConfig that pagination context is being reset (filter/sort/search change)
          apiConfig.onReset?.();
          // Only reset scroll when query context changed (search/sort/filter), not on server-side
          // page navigation — otherwise horizontal scroll snaps back on wide grids.
          if (resetGridScroll) {
            requestAnimationFrame(() => {
              try {
                apiRef.current?.scroll?.({ left: 0, top: 0 });
              } catch (e) {
                // Silently fail if scroll API unavailable
              }
            });
          }
        }

        setTotalRowCount(data.totalRowCount);
        setServerPage(serverPageToFetch);
        // Notify apiConfig of raw response so cursor-based skip values can be stored for the next page
        apiConfig.onRawDataLoaded?.(data.rawData, serverPageToFetch);
      } catch (err) {
        setError(err.message || intl.formatMessage({ id: 'dataGridErrorOccurred', defaultMessage: 'An error occurred' }));
        setFullData([]);
        setTotalRowCount(0);
        loadedServerPagesRef.current = new Set();
        setLoadedServerPages(new Set());
        setDataStartIndex(0);
      } finally {
        setLoading(false);
      }
    },
    [fetchDetails, dataStartIndex, pagerSize, apiConfig, apiRef, intl]
  );

  useEffect(() => {
    if (!normalizedInitialFilters.length) return;
    const serialized = JSON.stringify(normalizedInitialFilters);
    if (initialFiltersSerializedRef.current === serialized) return;
    initialFiltersSerializedRef.current = serialized;
    setFilters(normalizedInitialFilters);
    setDraftFilters(normalizedInitialFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // Reset accumulated data tracking when initial filters change
    loadedServerPagesRef.current = new Set();
    setLoadedServerPages(new Set());
    setDataStartIndex(0);
    // Don't fetch here: setFilters above already makes the main fetch effect below pick this up
    // and fetch once. Fetching here too (as this used to) double-fetched, every time.
  }, [normalizedInitialFilters]);

  /**
   * Effect to manage when to fetch data based on user interaction or data state.
   * Batch semantics: API returns a fixed batch (e.g. 200 or 50 items) per call. When the user
   * selects a smaller page size (e.g. 25), one batch serves multiple pages; when they select
   * a larger page size (e.g. 200) and pagerSize is smaller (e.g. 50), one UI page spans multiple
   * batches. We always fetch the first missing batch in the needed range so we fill forward
   * and accumulate (no oscillation). We accumulate only when the new batch is contiguous
   * with loaded batches; a direct jump to a non-adjacent page replaces the cache.
   */
  useEffect(() => {
    // Handle client data
    if (clientData && Array.isArray(clientData)) {
      setFullData(clientData);
      setTotalRowCount(clientData.length);
      setLoading(false);
      // Fetch user preferences for client-side grids (not called via fetchDetails in this mode)
      if (userPreferenceRequired === 'Yes' && !userPrefFetchedRef.current) {
        userPrefFetchedRef.current = true;
        fetchUserPreference();
      }
      if (onDataChange) {
        const currentRows = isClientSide ? getClientSideRows() : clientData;
        const currentPageOrgIds = currentRows.map((row) => row.id);
        onDataChange(currentPageOrgIds);
      }
      if (onRowsChange) {
        const currentRows = isClientSide ? getClientSideRows() : clientData;
        onRowsChange(currentRows);
      }
      return;
    }

    // Skip API calls if no apiKey (client-side only mode), unless caller supplies customFetch
    if (!apiConfig.apiKey && typeof apiConfig.customFetch !== 'function') {
      return;
    }

    if (!userFilterRestored) {
      return;
    }

    const clientIndex = paginationModel.page;
    const pageSize = paginationModel.pageSize;
    const clientRecordStart = clientIndex * pageSize;
    const clientRecordEnd = clientRecordStart + pageSize;

    // Calculate which server pages contain the needed records
    const startServerPage = Math.floor(clientRecordStart / pagerSize);
    const endServerPage = Math.floor((clientRecordEnd - 1) / pagerSize);

    // Check if the needed data is already loaded (within accumulated data range)
    const dataEndIndex = dataStartIndex + fullData.length;
    const needsDataBefore = clientRecordStart < dataStartIndex;
    // Check if we need more data: the actual needed range (capped at totalRowCount) extends beyond what we have
    const actualNeededEnd = Math.min(clientRecordEnd, totalRowCount);
    const needsDataAfter = actualNeededEnd > dataEndIndex && clientRecordStart < totalRowCount;

    // Determine if we need a fresh fetch (search, sort, filter changed) or accumulation
    // Use direct searchText comparison instead of substring matching to avoid false positives
    // (e.g., searching for "25" would incorrectly match "pageSize":25 in JSON string)
    const searchTextChanged = searchText !== lastFetchParams.current.searchText;
    // Use searchTextChanged alone so clearing search (searchText === "") also triggers re-fetch
    const needsFreshFetch = fullData.length === 0 || searchTextChanged;

    // Use ref to check loaded pages to avoid dependency on state that triggers re-renders
    const currentLoadedPages = loadedServerPagesRef.current;
    const needsAccumulation = isClientSide && (needsDataBefore || needsDataAfter) && !needsFreshFetch;

    // Determine which server page to fetch
    let serverPageToFetch;
    let shouldAccumulate = false;

    if (needsAccumulation) {
      // When one UI page spans multiple server batches (e.g. pagerSize 50, pageSize 200),
      // always fetch the first missing batch in [startServerPage, endServerPage] so we fill
      // forward and accumulate (0 then 1 then 2 then 3). Fetching endServerPage first would
      // replace with batch 3, then next run fetch 0 and replace again → oscillation.
      let firstMissingServerPage = null;
      for (let p = startServerPage; p <= endServerPage; p += 1) {
        if (!currentLoadedPages.has(p)) {
          firstMissingServerPage = p;
          break;
        }
      }
      serverPageToFetch = firstMissingServerPage ?? startServerPage;
      // Only accumulate when the new batch is contiguous with already loaded batches.
      // Direct jump to a non-adjacent batch replaces data.
      const isAdjacentToLoaded = currentLoadedPages.has(serverPageToFetch - 1) || currentLoadedPages.has(serverPageToFetch + 1);
      shouldAccumulate = !currentLoadedPages.has(serverPageToFetch) && isAdjacentToLoaded;
    } else {
      serverPageToFetch = isClientSide ? startServerPage : paginationModel.page;
    }

    // Prevent duplicate fetches with same parameters.
    // Include `apiKey` so switching the same grid to a different endpoint (e.g. org vs location
    // reboot schedules) still refetches even when `shouldFetch` is false due to cached rows.
    const fetchKey = JSON.stringify({
      apiKey: apiConfig?.apiKey ?? '',
      page: serverPageToFetch,
      pageSize,
      searchText,
      sortModel,
      filters,
      queryParams: queryParams || {},
      accumulate: shouldAccumulate
    });

    // Check if sortModel actually changed (for immediate sorting response)
    const sortModelKey = JSON.stringify(sortModel);
    const sortModelChanged = sortModelKey !== lastFetchParams.current.sortModelKey;

    // Reset accumulated data when sort/search/filter changes
    if (sortModelChanged || searchTextChanged) {
      loadedServerPagesRef.current = new Set();
      setLoadedServerPages(new Set());
      setDataStartIndex(0);
    }

    // Refetch whenever the fetch key or sort model changes. `apiKey` is part of `fetchKey` so
    // parent configs that only swap endpoints still invalidate the key (e.g. org vs location
    // reboot schedules on the same page component).
    const fetchKeyChanged = fetchKey !== lastFetchParams.current.key;
    if (fetchKeyChanged || sortModelChanged) {
      const resetGridScroll = searchTextChanged || sortModelChanged;
      lastFetchParams.current = { key: fetchKey, sortModelKey: sortModelKey, searchText: searchText };
      fetchDataWrapper(serverPageToFetch, searchText, sortModel, filters, queryParams, shouldAccumulate, resetGridScroll);
    }

    if (onDataChange) {
      // For client-side, get only current page rows; for server-side, fullData is already current page
      const currentRows = isClientSide ? getClientSideRows() : fullData;
      // Extract only the orgId (assuming 'id' is orgId)
      const currentPageOrgIds = currentRows.map((row) => row.id);
      onDataChange(currentPageOrgIds);
    }
    if (onRowsChange) {
      const currentRows = isClientSide ? getClientSideRows() : fullData;
      onRowsChange(currentRows);
    }
    // deliberately narrow: including the fetch helpers and row data here would refetch on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    searchText,
    sortModel,
    isClientSide,
    fullData.length,
    serverPage,
    pagerSize,
    queryParams,
    clientData,
    dataStartIndex,
    totalRowCount,
    apiConfig?.apiKey,
    filters,
    userFilterRestored
  ]);

  // Read the preference up front when it holds the saved sort and filters; the main fetch effect
  // waits for it. Marking it fetched stops fetchDetails from reading it a second time.
  useEffect(() => {
    if (!persistUserFilter || userPrefFetchedRef.current) return;
    userPrefFetchedRef.current = true;
    fetchUserPreference().finally(() => setUserFilterRestored(true));
    // mount-only: the preference is read once per grid instance, like the fetchDetails path
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetches user preference for columns, pinning, and density.
   */
  const fetchUserPreference = async () => {
    // Skip user preference fetch if userPreferenceAPIKey is not defined
    if (!apiConfig.userPreferenceAPIKey) {
      return;
    }

    try {
      const initialModel = {};
      const colByField = Object.fromEntries((columns || []).map((c) => [c.field, c]));
      columns.forEach((col) => {
        if (!(col.field in columnVisibilityModel)) {
          initialModel[col.field] = col.hide === true ? false : true;
        }
      });
      const response = await callApi(
        callingFrom == 'parOrg'
          ? 'GET_USER_PREFERENCE_PAR_ORG'
          : callingFrom == 'locationLevel'
            ? 'GET_USER_PREFERENCE_LOCATION_LEVEL'
            : 'GET_USER_PREFERENCE',
        {
          tableView: apiConfig.userPreferenceAPIKey,
          ...((callingFrom == 'parOrg' || callingFrom == 'locationLevel') && {
            orgId: localStorage.getItem('OrgId') ? localStorage.getItem('OrgId') : ''
          }),
          ...(callingFrom == 'locationLevel' && { networkId: JSON.parse(localStorage.getItem('selectedType')).id })
        },
        '',
        apiConfig.getHeaders?.() || []
      );
      if (response?.details?.showColumn) {
        const { showColumn, leftPin, rowDensity, inRowAction } = response.details;
        const updatedModel = Object.fromEntries(
          Object.keys(initialModel).map((key) => {
            if (key === 'actions') {
              // If showRowActionSettings is false, always show actions column regardless of preference
              // OR if the column has hideable: false, always show it
              const actionsCol = colByField[key];
              const shouldAlwaysShow = showRowActionSettings === false || actionsCol?.hideable === false;
              return [key, shouldAlwaysShow ? true : inRowAction === 'show'];
            }
            // Columns with hide: true (e.g. orgHealth) stay hidden after filter/preference
            if (colByField[key]?.hide === true) return [key, false];
            if (colByField[key]?.hideable === false) return [key, true];
            // A saved preference lists only the columns that were visible when it was saved, so a
            // column added to the grid later is absent from it and would be hidden for everyone
            // holding an older preference. Columns declared hideable: false are always shown, so
            // they cannot be dropped that way.
            return [key, showColumn.includes(key)];
          })
        );
        setColumnVisibilityModel(updatedModel);

        // Apply column order from showColumn - it contains visible columns in order.
        // A column added to the grid after this preference was saved is absent from showColumn.
        // Appending it would drop it at the far right, away from the columns it belongs beside, so
        // place it where the column list puts it: directly after the nearest preceding column the
        // saved order already knows. Falls back to the end when it has no such neighbour.
        const fullColumnOrder = [...showColumn];
        columns.forEach((col, index) => {
          if (col.field === 'actions' || fullColumnOrder.includes(col.field)) return;
          let insertAt = fullColumnOrder.length;
          for (let prev = index - 1; prev >= 0; prev--) {
            const anchor = fullColumnOrder.indexOf(columns[prev].field);
            if (anchor !== -1) {
              insertAt = anchor + 1;
              break;
            }
          }
          fullColumnOrder.splice(insertAt, 0, col.field);
        });
        setColumnOrder(fullColumnOrder);
        setOriginalColumnOrder([...fullColumnOrder]);

        setPinnedColumns((prev) => Array.from(new Set([...prev, ...leftPin])));
        if (rowDensity) setDensity(rowDensity);
        if (inRowAction) setRowAction(inRowAction);

        // Update backup state to match fetched preferences
        setOriginalColumnVisibilityModel({ ...updatedModel });
        setOriginalPinnedColumns(Array.from(new Set([...pinnedColumns, ...leftPin])));
        if (rowDensity) setOriginalDensity(rowDensity);
        if (inRowAction) setOriginalRowAction(inRowAction);
      }
      if (persistUserFilter && response?.details?.userFilter) {
        restoreUserFilter(response.details.userFilter);
      }
    } catch (error) {
      console.error('Error in get user preference api:', error);
    }
  };

  /**
   * Handles page change for pagination.
   * Clamps to valid range: if requested page > total pages, redirects to last valid page.
   * @param {object} _ - Event object (unused)
   * @param {number} value - New page number (1-based)
   */
  const handlePageChange = (_, value) => {
    const totalPages = Math.max(1, Math.ceil(totalRowCount / paginationModel.pageSize));
    const clampedPage = Math.max(1, Math.min(value, totalPages));
    const newPage = clampedPage - 1;
    const currentPage = paginationModel.page;

    if (sequentialPaginationOnly) {
      const isFirst = newPage === 0;
      const isNext = newPage === currentPage + 1;
      const isPrev = newPage === currentPage - 1;
      if (!isFirst && !isNext && !isPrev) return;
    }

    setPaginationModel((prev) => ({ ...prev, page: newPage }));
    if (onPaginationModelChange) {
      onPaginationModelChange({ page: newPage, pageSize: paginationModel.pageSize });
    }
  };

  /**
   * Handles page size change for pagination.
   * Resets to page 1 and clears batch cache so the correct batch is fetched for the new page size.
   * @param {object} e - Event object
   */
  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPaginationModel(() => ({
      page: 0,
      pageSize: newPageSize
    }));
    // Reset batch state so next effect fetches the correct batch for page 1
    loadedServerPagesRef.current = new Set();
    setLoadedServerPages(new Set());
    setDataStartIndex(0);
    setFullData([]);
  };

  /**
   * Handles quick filter input change.
   * @param {object} filterModel - Filter model from DataGrid
   */
  const handleFilterChange = (filterModel) => {
    const quickFilter = filterModel.quickFilterValues?.[0] || '';

    setSearchInput(quickFilter);
    setClientFilterText(quickFilter);

    // Cancel previous debounce timer if exists
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer: delay API search call by 500ms
    debounceTimerRef.current = setTimeout(() => {
      setSearchText(quickFilter); // Triggers API call via useEffect dependency
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
  };

  // usesSkipCursorPagination configs (e.g. alarms) key each page's cursor to the sort that
  // produced it, so staying on the current page after a sort change would reuse a stale cursor.
  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    saveUserFilter(newSortModel, filters);
    if (apiConfig?.usesSkipCursorPagination) {
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }
  };

  /**
   * Returns client-side filtered data for the current server chunk.
   * Applies column filters, quick filter and optional custom client filters before slicing.
   * Memoized to prevent unnecessary recalculations.
   */
  const clientSideFilteredData = useMemo(() => {
    let data = fullData;
    // Apply column filters for client-side operation
    if (operationSide === 'client' && filters.length > 0) {
      data = data.filter((row) => {
        return filters.every((filter) => {
          if (!filter.field || filter.value === undefined || filter.value === '') return true;
          const rowValue = String(row[filter.field] ?? '').toLowerCase();
          const filterValue = String(filter.value).toLowerCase();
          return rowValue.includes(filterValue);
        });
      });
    }
    // Apply quick text filter (case-insensitive) when operating on client
    if (operationSide === 'client' && clientFilterText) {
      const needle = String(clientFilterText).toLowerCase();
      data = data.filter((row) =>
        Object.values(row).some((value) =>
          String(value ?? '')
            .toLowerCase()
            .includes(needle)
        )
      );
    }
    // Allow caller to inject additional client-side filtering logic
    if (operationSide === 'client' && customFilterHandlers?.clientFilter) {
      try {
        data = customFilterHandlers.clientFilter(data, customFilterState) ?? data;
      } catch (error) {
        // if custom filter throws, fall back to current data
      }
    }
    return data;
  }, [fullData, operationSide, filters, clientFilterText, customFilterHandlers, customFilterState]);

  // Track previous clientFilter reference to detect when external filters change
  const prevClientFilterRef = useRef(customFilterHandlers?.clientFilter);

  // Reset pagination to page 0 when external client filter function changes
  // This ensures users see results from the beginning when filter criteria change,
  // even if the current page would still be valid with the new filtered data.
  useEffect(() => {
    if (!resetPaginationOnFilterChange) {
      return;
    }
    const currentFilter = customFilterHandlers?.clientFilter;
    if (prevClientFilterRef.current !== currentFilter) {
      prevClientFilterRef.current = currentFilter;
      // Only reset if we're not already on page 0
      if (paginationModel.page !== 0) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    }
  }, [resetPaginationOnFilterChange, customFilterHandlers?.clientFilter, paginationModel.page]);

  // Opt-in only: clamp client pagination when filtered rows no longer fill the current page
  // (fallback for cases where filter result shrinks but filter function reference unchanged).
  useEffect(() => {
    if (!resetPaginationOnFilterChange || operationSide !== 'client' || clientSideFilteredData.length === 0) {
      return;
    }
    const maxValidPage = Math.max(0, Math.ceil(clientSideFilteredData.length / paginationModel.pageSize) - 1);
    if (paginationModel.page > maxValidPage) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, [resetPaginationOnFilterChange, operationSide, clientSideFilteredData.length, paginationModel.pageSize, paginationModel.page]);

  // Keep the function for backward compatibility with other parts of the code
  const getClientSideFilteredData = () => clientSideFilteredData;

  const sortsAcrossPages = sortClientDataAcrossPages && operationSide === 'client';

  /**
   * With sortClientDataAcrossPages: the filtered rows in sort order, so the page slice below follows the sort
   * over every page. Ties keep their filtered order. Otherwise the filtered rows as they are.
   */
  const clientSideSortedData = useMemo(() => {
    const [sortItem] = sortModel || [];
    const column = sortItem?.sort && columns.find((col) => col.field === sortItem.field);
    if (!sortsAcrossPages || !column || column.sortable === false) {
      return clientSideFilteredData;
    }
    const direction = sortItem.sort === 'desc' ? -1 : 1;
    const typeComparator = getGridDefaultColumnTypes()[column.type || 'string']?.sortComparator;
    const baseComparator = column.sortComparator || typeComparator || gridStringOrNumberComparator;
    const comparator = column.getSortComparator?.(sortItem.sort) || ((v1, v2, p1, p2) => direction * baseComparator(v1, v2, p1, p2));
    const toEntry = (row, index) => {
      const value = column.valueGetter ? column.valueGetter(row[column.field], row, column, apiRef) : row[column.field];
      return { row, index, value, params: { id: row.id, field: column.field, value, api: apiRef.current } };
    };
    return clientSideFilteredData
      .map(toEntry)
      .sort((a, b) => comparator(a.value, b.value, a.params, b.params) || a.index - b.index)
      .map((entry) => entry.row);
  }, [sortsAcrossPages, sortModel, columns, clientSideFilteredData, apiRef]);

  /**
   * Memoized client-side rows for the current page.
   * Prevents unnecessary re-renders and scroll position resets.
   */
  const clientSideRows = useMemo(() => {
    const data = clientSideSortedData;
    // Calculate absolute start position for the current client page
    const absoluteStart = paginationModel.page * paginationModel.pageSize;
    // Convert to relative position within accumulated data
    const relativeStart = absoluteStart - dataStartIndex;
    const relativeEnd = relativeStart + paginationModel.pageSize;

    // Ensure we don't slice with negative indices
    const safeStart = Math.max(0, relativeStart);
    const safeEnd = Math.max(0, relativeEnd);

    return data.slice(safeStart, safeEnd);
  }, [clientSideSortedData, paginationModel.page, paginationModel.pageSize, dataStartIndex]);

  // Keep the function for backward compatibility with other parts of the code
  const getClientSideRows = () => clientSideRows;

  /**
   * Saves user preference for columns, pinning, and density.
   */
  const saveUserPreference = async ({
    columnVisibility = tempColumnVisibility,
    pinnedCols = tempPinnedColumns,
    rowDensityValue = tempDensity,
    rowActionValue = tempRowAction,
    colOrder = tempColumnOrder
  } = {}) => {
    // Get visible columns in their display order
    // If colOrder exists, use it to maintain order; otherwise fall back to orderedColumns
    const orderToUse = colOrder && colOrder.length > 0 ? colOrder : orderedColumns.map((col) => col.field);

    // Transform temp state for API - showColumn now contains visible columns in order
    const transformed = {
      showColumn: orderToUse.filter((key) => columnVisibility[key] && key !== 'actions'),
      leftPin: pinnedCols.filter((key) => columnVisibility[key] && key !== 'actions'),
      rowDensity: rowDensityValue,
      ...(showRowActionSettings && { inRowAction: rowActionValue }),
      ...(persistUserFilter && savedUserFilterRef.current && { userFilter: savedUserFilterRef.current })
    };

    try {
      showLoader();
      const response = await callApi(
        callingFrom === 'parOrg'
          ? 'SAVE_USER_PREFERENCE_PAR_ORG'
          : callingFrom === 'locationLevel'
            ? 'SAVE_USER_PREFERENCE_LOCATION_LEVEL'
            : 'SAVE_USER_PREFERENCE',
        {
          tableView: apiConfig.userPreferenceAPIKey,
          ...((callingFrom === 'parOrg' || callingFrom === 'locationLevel') && { orgId: localStorage.getItem('OrgId') || '' }),
          ...(callingFrom === 'locationLevel' && { networkId: JSON.parse(localStorage.getItem('selectedType')).id })
        },
        { data: transformed },
        apiConfig.getHeaders?.() || []
      );

      if (response?.response?.status) {
        hideLoader();
        setUserPrefSuccessOpen(true); // Show success message
        setTimeout(() => setUserPrefSuccessOpen(false), 3000);

        // Update real table state to match temp state; keep columns with hide: true hidden
        const normalizedVisibility = { ...columnVisibility };
        columns.forEach((col) => {
          if (col.hide === true) normalizedVisibility[col.field] = false;
        });
        setColumnVisibilityModel(normalizedVisibility);
        setPinnedColumns([...pinnedCols]);
        setDensity(rowDensityValue);
        if (showRowActionSettings) setRowAction(rowActionValue);
        setColumnOrder([...colOrder]);

        // Backup for cancel/reset
        setOriginalColumnVisibilityModel(normalizedVisibility);
        setOriginalPinnedColumns([...pinnedCols]);
        setOriginalDensity(rowDensityValue);
        if (showRowActionSettings) setOriginalRowAction(rowActionValue);
        setOriginalColumnOrder([...colOrder]);
      }
    } catch (error) {
      hideLoader();
      console.error('Error in save user preference api:', error);
    }

    setSettingsDrawerOpen(false); // close drawer
  };

  /**
   * Custom error overlay for DataGrid.
   * @param {object} error - Error message
   */
  const CustomErrorOverlay = ({ error }) => (
    <GridOverlay>
      <Typography color="error">{error}</Typography>
    </GridOverlay>
  );

  /**
   * Adds a new filter row. Uses the first filterable column not already selected in another row.
   * (Applies globally to all grids using the default filter drawer: device list, orgs, locations, etc.)
   * Does not add a row when all filterable columns are already used, to avoid blank/invalid filter payloads.
   */
  const addFilterRow = () => {
    const usedFields = new Set(draftFilters.map((f) => f.field).filter(Boolean));
    const firstCol = columns.find((col) => {
      const colName = col.filterColumnName ?? col.field;
      return (!col.hasOwnProperty('filterable') || col.filterable !== false) && colName && !usedFields.has(colName);
    });
    if (!firstCol) return;
    const newField = firstCol.filterColumnName ?? firstCol.field;
    setDraftFilters((prev) => [
      ...prev,
      { field: newField, operator: '=', value: getFirstOptionValue(firstCol?.filtersValue), filtersValue: firstCol?.filtersValue }
    ]);
  };

  /**
   * Removes a filter row by index.
   * @param {number} index - Index of filter row to remove
   */
  const removeFilterRow = (index) => {
    const newFilters = [...draftFilters];
    newFilters.splice(index, 1);
    setDraftFilters(newFilters);
  };

  /**
   * Removes all filter rows.
   */
  const removeAllFilters = () => {
    setFilters([]);
    setDraftFilters([]);
    saveUserFilter(sortModel, []);
    setFilterDrawerOpen(false);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // Reset accumulated data tracking when filters change
    loadedServerPagesRef.current = new Set();
    setLoadedServerPages(new Set());
    setDataStartIndex(0);
    lastFetchParams.current = { key: '', sortModelKey: '', searchText: '' };
    // For client-side operation, filters are applied in getClientSideFilteredData
    // Only call fetchDataWrapper for server-side operation
    if (operationSide !== 'client') {
      fetchDataWrapper(0, searchText, sortModel, [], queryParams, false, true);
    }
  };

  /**
   * Handles filter value change.
   * @param {number} index - Index of filter row
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const handleFilterValueChange = (rowIndex, propName, propValue) => {
    setDraftFilters((prevFilters) => {
      const updatedFilters = [...prevFilters];
      const currentFilter = { ...updatedFilters[rowIndex] };
      currentFilter[propName] = propValue;

      // When the selected column changes, refresh available values and reset value
      if (propName === 'field') {
        const selectedColumn = columns.find((col) => col.filterColumnName === propValue);
        currentFilter.filtersValue = selectedColumn?.filtersValue || [];
        currentFilter.value = getFirstOptionValue(currentFilter.filtersValue);
        const hasOptions = Array.isArray(currentFilter.filtersValue) && currentFilter.filtersValue.length > 0;
        if (hasOptions) {
          currentFilter.operator = '=';
        }
      }

      updatedFilters[rowIndex] = currentFilter;
      return updatedFilters;
    });
  };

  /**
   * Applies filters and closes the filter drawer.
   */
  const applyFilters = () => {
    const appliedFilters = draftFilters.map((item) => ({ ...item }));
    setFilters(appliedFilters);
    // Reset accumulated data tracking when filters change
    loadedServerPagesRef.current = new Set();
    setLoadedServerPages(new Set());
    setDataStartIndex(0);
    lastFetchParams.current = { key: '', sortModelKey: '', searchText: '' };

    if (customFilterHandlers?.applyFilters) {
      // Use custom filter handler if provided
      customFilterHandlers.applyFilters(customFilterState, () => {
        setFilterDrawerOpen(false);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        if (operationSide !== 'client') {
          fetchDataWrapper(0, searchText, sortModel, [], queryParams, false, true);
        }
      });
      return;
    }
    // Generic column filter mode. Only this mode is persisted: custom handlers keep their own filter state.
    saveUserFilter(sortModel, appliedFilters);
    setFilterDrawerOpen(false);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // For client-side operation, filters are applied in getClientSideFilteredData
    // Only call fetchDataWrapper for server-side operation
    if (operationSide !== 'client') {
      fetchDataWrapper(0, searchText, sortModel, appliedFilters, queryParams, false, true);
    }
  };

  /**
   * Generic custom filter handlers
   */
  const handleCustomFilterSelect = (type, value, isSelected) => {
    if (customFilterHandlers?.handleFilterSelect) {
      customFilterHandlers.handleFilterSelect(
        type,
        value,
        isSelected,
        customFilterState,
        setCustomFilterState,
        customFilterData,
        setCustomFilterData
      );
    }
  };

  const clearCustomFilters = async () => {
    if (customFilterHandlers?.clearFilters) {
      await customFilterHandlers.clearFilters(() => {
        setFilterDrawerOpen(false);
        fetchDataWrapper(0, searchText, sortModel, [], queryParams, false, true);
      });
    }
  };

  /**
   * Filter Chips Handle
   **/
  const getFilterLabel = (filter) => {
    const column = columns.find((col) => col.filterColumnName === filter.field);
    const filtersValue = filter.filtersValue || column?.filtersValue;
    const displayValue = getFilterDisplayValue(filtersValue, filter.value);

    return `${getColumnDisplayLabel(column) || filter.field}: ${displayValue}`;
  };

  const removeFilterChip = (indexToRemove) => {
    const updatedFilters = filters.filter((_, index) => index !== indexToRemove);

    setFilters(updatedFilters);
    setDraftFilters(updatedFilters);
    saveUserFilter(sortModel, updatedFilters);

    setPaginationModel((prev) => ({
      ...prev,
      page: 0
    }));

    loadedServerPagesRef.current = new Set();
    setLoadedServerPages(new Set());
    setDataStartIndex(0);

    if (operationSide !== 'client') {
      fetchDataWrapper(0, searchText, sortModel, updatedFilters, queryParams, false, true);
    }
  };

  const handleSearchInputChange = (e) => {
    const inputValue = e.target.value;
    // Prevent leading spaces but allow trailing and middle spaces
    const value = inputValue.replace(/^\s+/, '');
    setSearchInput(value);
    if (operationSide == 'client') {
      setClientFilterText(value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      return;
    }

    // Cancel previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer for server-side search (500ms delay)
    debounceTimerRef.current = setTimeout(() => {
      if (value.length >= minSearchLength) {
        setSearchText(value);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      } else if (value.length === 0) {
        setSearchText('');
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    }, 500);
  };

  // Calculate pagination page count.
  // - Server-side: use API-reported total count
  // - Client-side: reflect filtered data in the current chunk so pages don't appear empty
  const getPaginationPageCount = () => {
    if (isClientSide && operationSide === 'client') {
      const filteredLength = getClientSideFilteredData().length;
      return Math.max(1, Math.ceil(filteredLength / paginationModel.pageSize));
    }
    return Math.max(1, Math.ceil(totalRowCount / paginationModel.pageSize));
  };

  // Get actual number of rows being displayed
  const getActualRowCount = () => {
    try {
      if (isClientSide) {
        const rows = getClientSideRows();
        return rows.length;
      }
      return fullData.length;
    } catch {
      return 0;
    }
  };

  // Calculate height for DataGrid based on actual rows to prevent white space
  const getDataGridHeight = () => {
    // Column header height
    const headerHeight = 56;

    // Get actual number of rows being displayed
    const actualRowCount = getActualRowCount();

    // When there are no rows (empty state), use minimal height for the "No data available" overlay
    if (!loading && actualRowCount === 0 && totalRowCount === 0) {
      // Height for empty state: header + empty overlay content (image + text + padding)
      // Empty overlay needs: ~140px for image + text + increased padding (pt: 4 = 32px, pb: 3 = 24px, gap: 16px)
      return headerHeight + 140 + 130; // 130px padding
    }

    // Row heights based on density
    const rowHeights = {
      compact: 36,
      standard: 52,
      comfortable: 72
    };
    const currentRowHeight = rowHeight || rowHeights[density] || rowHeights.standard;

    // During loading, use pageSize to prevent flicker
    // Otherwise, use actual row count to avoid white space
    const rowsToShow = loading ? paginationModel.pageSize : Math.min(actualRowCount, paginationModel.pageSize);

    const rowsHeight = rowsToShow * currentRowHeight;
    const calculatedHeight = headerHeight + rowsHeight + 8; // 8px padding

    return calculatedHeight;
  };

  // Calculate skeleton rows that fit within viewport during loading
  const getSkeletonRowCount = () => {
    const rowHeights = {
      compact: 36,
      standard: 52,
      comfortable: 72
    };
    const currentRowHeight = rowHeight || rowHeights[density] || rowHeights.standard;

    // Get viewport height
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

    // Reserve space for UI chrome (header, breadcrumbs, action bar, table header, pagination, margins)
    const reservedSpace = 350;

    // Calculate available space for skeleton rows
    const availableHeight = viewportHeight - reservedSpace;
    const maxSkeletonRows = Math.floor(availableHeight / currentRowHeight);

    // Minimum 3 rows, maximum 10 rows
    return Math.max(3, Math.min(maxSkeletonRows, 10));
  };

  // Calculate minimum height for DataGrid to prevent layout shift during loading
  const getMinHeight = () => {
    // During loading, use fixed skeleton rows to fit in viewport
    // Otherwise, use calculated height based on actual rows
    if (loading) {
      const rowHeights = {
        compact: 36,
        standard: 52,
        comfortable: 72
      };
      const currentRowHeight = rowHeight || rowHeights[density] || rowHeights.standard;
      const headerHeight = 56;
      const rowsHeight = getSkeletonRowCount() * currentRowHeight;
      return headerHeight + rowsHeight + 8;
    }
    return getDataGridHeight();
  };

  // Calculate overlay height (content area only, excluding header)
  const getOverlayHeight = () => {
    // Get actual number of rows being displayed
    const actualRowCount = getActualRowCount();

    // When there are no rows (empty state), use minimal height for the "No data available" overlay
    if (!loading && actualRowCount === 0 && totalRowCount === 0) {
      // Empty overlay needs: ~140px for image + text + increased padding (pt: 4 = 32px, pb: 3 = 24px, gap: 16px)
      return 362;
    }

    const rowHeights = {
      compact: 36,
      standard: 52,
      comfortable: 72
    };
    const currentRowHeight = rowHeight || rowHeights[density] || rowHeights.standard;

    // During loading, use viewport-based skeleton rows; otherwise use actual row count
    const rowsToShow = loading ? getSkeletonRowCount() : Math.min(actualRowCount, paginationModel.pageSize);

    const rowsHeight = rowsToShow * currentRowHeight;
    return rowsHeight;
  };

  const [_openSearch, setOpenSearch] = useState(() => Boolean(trimmedInitialSearchText));
  // const [searchInput, setSearchInput] = useState("");

  // const handleSearchInputChange = (e) => setSearchInput(e.target.value);
  const _toggleSearch = () => {
    setOpenSearch((prev) => {
      const newOpenState = !prev;
      // If closing the search bar, reset search state
      if (!newOpenState) {
        setSearchInput('');
        setSearchText('');
        setClientFilterText('');
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
      return newOpenState;
    });
  };

  const escapeCsvValue = (value) => {
    return `"${String(value ?? '').replace(/"/g, '""')}"`;
  };

  const resolveDownloadTitle = () => {
    if (pageTitle) {
      const isTranslationKey = typeof pageTitle === 'string' && /^[a-z][a-zA-Z0-9]*$/.test(pageTitle) && !pageTitle.includes(' ');

      return isTranslationKey ? intl.formatMessage({ id: pageTitle, defaultMessage: pageTitle }) : pageTitle;
    }

    const headingEl = document.querySelector('main .MuiContainer-root h2');
    return headingEl?.textContent?.trim() || '';
  };

  const handleDownload = async () => {
    if (!fullData?.length || csvExporting) return;

    // Export only visible columns
    const visibleColumns = orderedColumns.filter((col) => col.field !== 'actions' && effectiveColumnVisibility[col.field] !== false);
    const exportColumns = getExportColumns?.(visibleColumns) ?? visibleColumns;

    // `csvPrepare(rows)` lets a column fetch its values for every exported row
    // first — the export spans rows that never rendered. Failures fall back.
    const preparers = exportColumns.filter((col) => typeof col.csvPrepare === 'function');
    // Swallows a rejection and a synchronous throw alike, so the file still writes.
    const runPreparer = async (col) => {
      try {
        await col.csvPrepare(fullData);
      } catch (err) {
        // values fall back per row
      }
    };
    if (preparers.length) {
      setCsvExporting(true);
      try {
        await Promise.all(preparers.map(runPreparer));
      } finally {
        setCsvExporting(false);
      }
    }

    const headers = exportColumns.map((col) => escapeCsvValue(getColumnDisplayLabel(col) || col.field));

    // `csvValueGetter(row)` exports what renderCell displays (chips, fallbacks)
    // instead of the raw — often empty — underlying field.
    const rows = fullData.map((row) =>
      exportColumns
        .map((col) => escapeCsvValue(typeof col.csvValueGetter === 'function' ? col.csvValueGetter(row) : row[col.field]))
        .join(',')
    );

    const csvContent = [headers.join(','), ...rows].join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = buildCsvExportFileName(resolveDownloadTitle());

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  /**
   * Handles refresh button click - resets search and pagination, then refetches data.
   * Sort: clears to no sort unless the parent set `initialSortModel`, in which case that
   * default is restored (keeps column sort indicator and server sort in sync after refresh).
   */
  const handleRefresh = () => {
    // Reset search states
    setSearchText('');
    setSearchInput('');
    setClientFilterText('');

    // Close search box if open
    setOpenSearch(false);

    const model = initialSortModelRef.current;
    const refreshSortModel = Array.isArray(model) && model.length > 0 ? model.map((item) => ({ ...item })) : [];
    // A persisted sort is a saved preference, so refresh keeps it. Still set a new array: the fetch
    // effect below only re-runs when one of its dependencies changes.
    setSortModel(persistUserFilter ? sortModel.map((item) => ({ ...item })) : refreshSortModel);

    // Reset pagination to initial page
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    // Reset accumulated data tracking
    loadedServerPagesRef.current = new Set();
    setLoadedServerPages(new Set());
    setDataStartIndex(0);
    lastFetchParams.current = { key: '', sortModelKey: '', searchText: '' };

    // Client-driven grid: parent owns data and must reload from backend; grid will receive fresh clientData on next render
    if (clientData && typeof onRefresh === 'function') {
      onRefresh();
      return;
    }

    // If using clientData with no apiKey and no onRefresh (client-side only mode),
    // just refresh from clientData instead of trying to fetch from API
    if (clientData && !apiConfig.apiKey) {
      setFullData(clientData);
      setTotalRowCount(clientData.length);
      return;
    }

    // Don't fetch here: the reset sortModel/paginationModel above already make the main fetch
    // effect below picks this up and fetches once. Fetching here too used to double-fetch.
  };

  // Temporary states
  const [tempColumnVisibility, setTempColumnVisibility] = useState(columnVisibilityModel);
  const [tempPinnedColumns, setTempPinnedColumns] = useState(pinnedColumns);
  const [tempDensity, setTempDensity] = useState(density);
  const [tempRowAction, setTempRowAction] = useState(rowAction);
  const [tempColumnOrder, setTempColumnOrder] = useState(columnOrder);

  // ===== Effective values (live preview while drawer is open) =====
  const effectiveColumnVisibility = settingsDrawerOpen ? tempColumnVisibility : columnVisibilityModel;

  const effectivePinnedColumns = settingsDrawerOpen ? tempPinnedColumns : pinnedColumns;

  const effectiveDensity = settingsDrawerOpen ? tempDensity : density;

  const _effectiveRowAction = settingsDrawerOpen ? tempRowAction : rowAction;

  // Reset temporary states when drawer opens
  useEffect(() => {
    if (settingsDrawerOpen) {
      setTempColumnVisibility(columnVisibilityModel);
      setTempPinnedColumns(pinnedColumns);
      setTempDensity(density);
      setTempRowAction(rowAction);
      setTempColumnOrder(columnOrder.length > 0 ? columnOrder : orderedColumns.map((col) => col.field));
    }
    // snapshots the applied settings when the drawer opens; including them would discard in-drawer edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsDrawerOpen]);

  // Sync tempColumnOrder when columnOrder changes (e.g., from drag-and-drop while drawer is open)
  useEffect(() => {
    if (settingsDrawerOpen && columnOrder.length > 0) {
      setTempColumnOrder([...columnOrder]);
    }
  }, [columnOrder, settingsDrawerOpen]);

  // Temporary handlers
  const handleToggleColumnTemp = (field) => {
    setTempColumnVisibility((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleTogglePinTemp = (field) => {
    setTempPinnedColumns((prev) => {
      if (prev.includes(field)) return prev.filter((f) => f !== field);
      const pinnedUserColumns = prev.filter((f) => f !== '__check__');
      if (pinnedUserColumns.length >= MAX_LEFT_PINNED_ITEMS) {
        setPinLimitAlertOpen(true);
        return prev;
      }
      return [...prev, field];
    });
  };

  const handleDensityTemp = (value) => setTempDensity(value);

  const handleRowActionTemp = (value) => {
    setTempRowAction(value);
    setTempColumnVisibility((prev) => ({
      ...prev,
      actions: value === 'show'
    }));
  };

  // Closing drawer without saving
  const handleCancelDrawer = () => {
    setTempColumnVisibility({ ...columnVisibilityModel });
    setTempPinnedColumns([...pinnedColumns]);
    setTempDensity(density);
    setTempRowAction(rowAction);
    setSettingsDrawerOpen(false);
  };

  const STORAGE_KEY = rest.id ? `grid-column-widths-${rest.id}` : null;

  const [columnWidths, setColumnWidths] = useState(() => {
    if (!STORAGE_KEY) {
      return {};
    }

    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const processedColumns = useMemo(() => {
    return orderedColumns.map((col) => {
      const savedWidth = columnWidths[col.field];

      // If user resized column
      if (savedWidth) {
        return {
          ...col,
          width: savedWidth,
          flex: undefined,
          minWidth: col.minWidth || 80
        };
      }

      // untouched columns behave normally
      return col;
    });
  }, [orderedColumns, columnWidths]);

  const displayedCount = Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalRowCount);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      document.querySelectorAll('.MuiDataGrid-sortButton[title]').forEach((btn) => btn.removeAttribute('title'));
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  return (
    (<Box {...rest} id={rest.id || 'dataGridRoot'} data-testid={rest['data-testid'] || 'dataGridRoot'}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} id="dataGridHeaderStack">
        {/* Left side: Add, Edit, Delete */}
        <Stack direction="row" spacing={1} id="dataGrid-actionStack">
          {children}
        </Stack>
        {/* Right side: Filter and Settings */}
        <Stack direction="row" alignItems="center" spacing={0} id="dataGridSearchStack">
          {rightHeaderChildren}
          {showSearchBox && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: 1,
                mr: 1
              }}
            >
              <TextField
                size="small"
                placeholder={searchPlaceholder || intl.formatMessage({ id: 'dataGridSearchPlaceholder', defaultMessage: 'Search...' })}
                value={searchInput}
                onChange={handleSearchInputChange}
                id="dataGridSearchInput"
                data-field-label={intl.formatMessage({ id: 'search', defaultMessage: 'Search' })}
                data-type="text"
                className="inputBackTransparent"
                sx={{
                  width: { xs: '180px', md: '240px' }
                }}
              />
            </Box>
          )}

          {filterRequired === 'Yes' && (
            <Tooltip title={intl.formatMessage({ id: 'filter', defaultMessage: 'Filter' })} placement="top">
              <IconButton
                onClick={toggleFilterDrawer}
                id="dataGridFilterBtn"
                aria-label={intl.formatMessage({ id: 'filter', defaultMessage: 'Filter' })}
                sx={{
                  color: isFilterApplied ? 'teal.500' : 'inherit',
                  backgroundColor: isFilterApplied && theme.palette.mode === 'light' ? 'bluegrey.light' : 'transparent',
                  border: '1px solid',
                  borderColor: isFilterApplied ? 'teal.500' : 'transparent',
                  borderRadius: isFilterApplied ? '6px' : '50%',
                  p: 0.5
                }}
              >
                <IconAdjustmentsHorizontal stroke={1.2} size={24} id="DataGridFilterIcon" />
              </IconButton>
            </Tooltip>
          )}

          {refreshRequired === 'Yes' && (
            <Tooltip title={intl.formatMessage({ id: 'refresh', defaultMessage: 'Refresh' })} placement="top">
              <IconButton
                onClick={handleRefresh}
                id="dataGridRefreshBtn"
                aria-label={intl.formatMessage({ id: 'refresh', defaultMessage: 'Refresh' })}
                disabled={loading}
                sx={{
                  p: 0.5,
                  ml: 0.5
                }}
              >
                <Box
                  component="span"
                  id="dataGridRefreshIcon"
                  sx={{
                    display: 'inline-flex',
                    animation: loading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                >
                  <IconReload size={22} stroke={1.5} />
                </Box>
              </IconButton>
            </Tooltip>
          )}

          {showDownloadButton && (
            <Tooltip title={intl.formatMessage({ id: 'downloadCSV', defaultMessage: 'Download CSV' })} placement="top">
              <IconButton
                onClick={onDownload || handleDownload}
                disabled={csvExporting}
                id="dataGridDownloadBtn"
                aria-label={intl.formatMessage({ id: 'downloadCSV', defaultMessage: 'Download CSV' })}
                sx={{
                  p: 0.5,
                  ml: 0.5
                }}
              >
                {csvExporting ? (
                  <CircularProgress size={22} thickness={4} id="dataGridExportSpinner" />
                ) : (
                  <IconDownload stroke={1.5} size={22} id="dataGridExportIcon" />
                )}
              </IconButton>
            </Tooltip>
          )}

          {settingsRequired == 'Yes' && (
            <Tooltip title={intl.formatMessage({ id: 'settings', defaultMessage: 'Settings' })} placement="top">
              <IconButton
                onClick={toggleSettingsDrawer}
                id="dataGridSettingsBtn"
                aria-label={intl.formatMessage({ id: 'settings', defaultMessage: 'Settings' })}
                sx={{ display: showSearchBox ? 'flex' : 'none', p: 0.5, ml: 0.5 }}
              >
                <IconSettings id="dataGridSettingsIcon" stroke={1.5} size={25} />
              </IconButton>
            </Tooltip>
          )}
          {afterSettingsChildren}
        </Stack>
      </Stack>
      {showFilterChips && filters.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2
          }}
        >
          {filters.map((filter, index) => (
            <Chip
              key={`${filter.field}-${index}`}
              label={getFilterLabel(filter)}
              onDelete={() => removeFilterChip(index)}
              size="small"
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? 'bluegrey.light' : 'teal.900',
                color: alpha(theme.palette.text.dark, 0.9),
                '&.MuiChip-root .MuiChip-deleteIcon': {
                  color: 'grey.500',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'grey.700'
                  }
                }
              }}
            />
          ))}
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mr: 0.5,
              ml: 1,
              height: 24,
              alignSelf: 'center',
              borderColor: 'grey.300'
            }}
          />
          <Chip
            label={intl.formatMessage({ id: 'clearAll', defaultMessage: 'Clear All' })}
            onClick={removeAllFilters}
            size="small"
            variant="filled"
            sx={{
              border: 'none',
              backgroundColor: 'transparent',
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          minHeight: loading ? `${getMinHeight()}px` : undefined,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          maxHeight: 'calc(100vh - 332px)',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
        id="dataGridContainer"
      >
        <DataGridPro
          apiRef={apiRef}
          rows={isClientSide ? getClientSideRows() : fullData}
          rowCount={totalRowCount}
          columns={processedColumns}
          loading={loading}
          pinnedColumns={{ left: effectivePinnedColumns, right: ['actions'] }}
          disableColumnReorder={false}
          keepColumnPositionIfDraggedOutside={true}
          onColumnOrderChange={handleColumnOrderChange}
          {...(rowHeight && { rowHeight })}
          {...(getRowHeight && { getRowHeight })}
          {...(getRowClassName && { getRowClassName })}
          onColumnWidthChange={(params) => {
            const updatedWidths = {
              ...columnWidths,
              [params.colDef.field]: params.width
            };

            setColumnWidths(updatedWidths);

            if (STORAGE_KEY) {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedWidths));
            }
          }}
          pagination
          paginationMode="server"
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          filterMode={operationSide}
          {...(operationSide == 'client' && { filterModel: { items: [], quickFilterValues: clientFilterText ? [clientFilterText] : [] } })}
          onFilterModelChange={handleFilterChange}
          // Rows sorted across pages arrive in order (clientSideSortedData); DataGridPro only shows the sort
          sortingMode={sortsAcrossPages ? 'server' : operationSide}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          disableMultipleColumnsSorting
          disableRowSelectionOnClick
          disableColumnFilter
          hideFooter
          checkboxSelection={showCheckbox}
          {...(() => {
            const resolvedIsRowSelectable =
              typeof isRowSelectable === 'function'
                ? isRowSelectable
                : isRowSelectable === false || !showCheckbox
                  ? () => false
                  : () => totalRowCount > 0;
            return { isRowSelectable: resolvedIsRowSelectable };
          })()}
          onRowClick={(params, event) => {
            if (typeof onRowClick === 'function') {
              // Avoid triggering when clicking the checkbox column
              const target = event?.target;
              const isCheckbox = target && target.closest && target.closest('.MuiCheckbox-root');
              if (!isCheckbox) onRowClick(params.row, params, event);
            }
          }}
          onRowDoubleClick={(params, event) => {
            if (typeof onRowDoubleClick === 'function') {
              onRowDoubleClick(params.row, params, event);
            } else if (typeof onRowClick === 'function') {
              onRowClick(params.row, params, event);
            }
          }}
          isRowSelectable={isRowSelectable || (() => totalRowCount > 0)}
          {...(rowSelectionModel !== undefined && { rowSelectionModel })}
          {...(disableRowSelectionExcludeModel && { disableRowSelectionExcludeModel })}
          {...(keepNonExistentRowsSelected && { keepNonExistentRowsSelected })}
          onRowSelectionModelChange={(ids) => {
            // When controlled (rowSelectionModel provided), count is synced from prop in useEffect only.
            // Do not update from grid callback here—grid reports current-page selection, which would
            // wrongly show 0 when on a page with no selected rows even if other pages have selection.
            if (rowSelectionModel === undefined) {
              if (ids && typeof ids === 'object') {
                if (ids.type === 'include' && ids.ids) {
                  const count = Array.isArray(ids.ids) ? ids.ids.length : ids.ids instanceof Set ? ids.ids.size : 0;
                  setSelectedRowCount(count);
                } else if (ids.type === 'exclude' && ids.ids) {
                  const excludedCount = Array.isArray(ids.ids) ? ids.ids.length : ids.ids instanceof Set ? ids.ids.size : 0;
                  setSelectedRowCount(Math.max(0, totalRowCount - excludedCount));
                } else if (Array.isArray(ids)) {
                  setSelectedRowCount(ids.length);
                }
              } else if (Array.isArray(ids)) {
                setSelectedRowCount(ids.length);
              } else {
                setSelectedRowCount(0);
              }
            }

            if (totalRowCount > 0 && onRowSelectionModelChange) {
              onRowSelectionModelChange(ids, isClientSide ? getClientSideRows() : fullData);
            }
          }}
          columnVisibilityModel={effectiveColumnVisibility}
          onColumnVisibilityModelChange={(model) => {
            // Keep columns with hide: true (e.g. orgHealth) hidden every time
            const normalized = { ...model };
            columns.forEach((col) => {
              if (col.hide === true) normalized[col.field] = false;
            });
            if (settingsDrawerOpen) {
              setTempColumnVisibility(normalized);
            } else {
              setColumnVisibilityModel(normalized);
            }
          }}
          density={effectiveDensity}
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          {...(getDetailPanelContent && { getDetailPanelContent })}
          {...(getDetailPanelHeight && { getDetailPanelHeight })}
          {...(detailPanelExpandedRowIds && { detailPanelExpandedRowIds })}
          {...(onDetailPanelExpandedRowIds && { onDetailPanelExpandedRowIdsChange: onDetailPanelExpandedRowIds })}
          slots={{
            toolbar: GridToolbar,
            // Opt-in detail-panel toggle: vertical chevrons (Figma) instead of the MUI X default +/-.
            // Gated on useChevronDetailPanelIcons so only the stacking Devices table changes; other
            // detail-panel grids (e.g. NotificationAlarmsGrid) keep the default glyphs. Chevron-down
            // when collapsed (expand), chevron-up when open (collapse).
            ...(useChevronDetailPanelIcons && {
              detailPanelExpandIcon: ExpandMoreIcon,
              detailPanelCollapseIcon: ExpandLessIcon
            }),
            noRowsOverlay: () => (
              <Typography
                variant="subtitle"
                component="p"
                sx={{
                  pt: 4,
                  pb: 3,
                  px: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  borderTop: 'solid 1px',
                  borderColor: 'divider',
                  color: 'grey.600'
                }}
                id="dataGridNoRows"
              >
                <img src={emptyTableImage} alt="" width="80" />
                {resolvedNoRowsOverlayLabel}
              </Typography>
            ),
            errorOverlay: () => <CustomErrorOverlay error={error} />
          }}
          sx={[
            {
              '--DataGrid-overlayHeight': `${getOverlayHeight()}px`,
              height: loading ? `${getMinHeight()}px` : 'auto',
              '& .MuiDataGrid-columnHeaders': {
                fontWeight: '500',
                position: 'sticky',
                top: 0,
                zIndex: 5,
                backgroundColor: theme.palette.background.paper
              },
              '& .unread-row': {
                fontWeight: '500'
              },
              '& .device-credit-cell': {
                display: 'flex',
                alignItems: 'center'
              },
              '& .MuiDataGrid-cellEmpty': {
                display: 'none'
              },
              ...(totalRowCount === 0 && {
                '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
                  opacity: 0.5,
                  pointerEvents: 'none'
                }
              }),
              '& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
                outlineOffset: '0px'
              },
              '& .MuiDataGrid-columnHeader:focus': {
                outline: 'none'
              },
              "& .MuiDataGrid-cell[data-field='actions'] > div": {
                cursor: 'pointer'
              },
              "& .MuiDataGrid-cell[data-field='actions'] > div svg": {
                cursor: 'pointer'
              },
              '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root, & .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
                // reduce padding
                '& .MuiSvgIcon-root': {
                  fontSize: '22px' // smaller checkbox icons
                },
                '& .MuiTouchRipple-root': {
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) scale(0.8)' // center ripple
                }
              },
              fontSize: theme.typography.body1.fontSize,
              color: theme.palette.grey[800]
            },
            rest.sx
          ]}
          id="dataGridTable"
        />
      </Box>
      {/* Custom footer for pagination controls */}
      {!hidePagination && (
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          px={2}
          py={1}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: theme.palette.background.paper
          }}
          id="dataGridFooter"
        >
          <Typography variant="caption" color="text.secondary">
            {intl.formatMessage(
              { id: 'dataGridShowingOfItems', defaultMessage: 'Showing {shown} of {total} items' },
              { shown: displayedCount, total: totalRowCount }
            )}
          </Typography>
          <Box display="flex" alignItems="left" justifyContent={{ xs: 'space-between', md: 'unset' }} gap={2} id="dataGridFooterLeft">
            <Box display="flex" alignItems="center" id="dataGridRowsPerPageBox">
              <Typography variant="caption" sx={{ mr: 1 }} id="dataGridRowsPerPageLabel">
                {intl.formatMessage({ id: 'rowsPerPage', defaultMessage: 'Rows per page' })}:
              </Typography>
              <FormControl size="small" variant="standard">
                <Select
                  id="dataGridRowsPerPageSelect"
                  data-field-label={intl.formatMessage({ id: 'rowsPerPage', defaultMessage: 'Rows per page' })}
                  data-type="select"
                  value={paginationModel.pageSize}
                  onChange={handlePageSizeChange}
                  displayEmpty
                  sx={{
                    fontSize: theme.typography.caption.fontSize,
                    '& .MuiSelect-select': {
                      padding: '2px 4px',
                      letterSpacing: '1px',
                      borderBottom: 'none'
                    },
                    '&:hover:not(.Mui-disabled, .Mui-error):before': {
                      borderBottom: 'none'
                    },
                    '&:hover:before, ::before, ::after ': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {sequentialPaginationOnly ? (
              <Stack direction="row" alignItems="center" spacing={0.5} id="dataGridSequentialPagination">
                <IconButton
                  size="small"
                  aria-label={intl.formatMessage({ id: 'pagination-first-page', defaultMessage: 'First page' })}
                  onClick={() => handlePageChange(null, 1)}
                  disabled={paginationModel.page === 0 || loading}
                  id="dataGridPaginationFirst"
                >
                  <FirstPageIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={intl.formatMessage({ id: 'pagination-previous-page', defaultMessage: 'Previous page' })}
                  onClick={() => handlePageChange(null, paginationModel.page)}
                  disabled={paginationModel.page === 0 || loading}
                  id="dataGridPaginationPrev"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ px: 1, minWidth: '7rem', textAlign: 'center' }} id="dataGridPaginationPageLabel">
                  {intl.formatMessage(
                    { id: 'pagination-page-of', defaultMessage: 'Page {current} of {total}' },
                    { current: paginationModel.page + 1, total: getPaginationPageCount() }
                  )}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={intl.formatMessage({ id: 'pagination-next-page', defaultMessage: 'Next page' })}
                  onClick={() => handlePageChange(null, paginationModel.page + 2)}
                  // Also gated on `loading`: fetchDataWrapper has no request sequencing, so clicking
                  // next again before the current fetch (e.g. a new correlatedSkip/legacySkip batch)
                  // resolves can let responses land out of order.
                  disabled={paginationModel.page + 1 >= getPaginationPageCount() || loading}
                  id="dataGridPaginationNext"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <Pagination
                count={getPaginationPageCount()}
                page={paginationModel.page + 1}
                onChange={handlePageChange}
                className="custom-pagination"
                id="dataGridPagination"
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1,
                    minWidth: '26px',
                    height: '26px',
                    fontSize: theme.typography.body1.fontSize
                  }
                }}
              />
            )}
          </Box>
        </Box>
      )}
      {/* Settings Drawer */}
      <CommonDrawer
        open={settingsDrawerOpen}
        onClose={handleCancelDrawer}
        PaperProps={{
          sx: { width: { xs: 'calc(100% - 20px)', sm: '480px' } }
        }}
        id="dataGridSettingsDrawer"
      >
        {/* ===== Drawer Header ===== */}
        <DrawerHeader sx={{ mb: 1 }} id="dataGridSettingsDrawerHeader">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h3" id="dataGridSettingsDrawerTitle">
              <FormattedMessage id="dataGridTableSettingsTitle" defaultMessage="Table Settings" />
            </Typography>
            <IconButton
              onClick={handleCancelDrawer}
              size="small"
              id="dataGridSettingsDrawerCloseBtn"
              aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
            >
              <CloseIcon id="dataGridSettingsDrawerCloseIcon" />
            </IconButton>
          </Box>
        </DrawerHeader>

        {/* ===== Tabs ===== */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2, pl: 1.7 }} id="dataGridSettingsTabs">
          <Tab
            label={intl.formatMessage({ id: 'dataGridColumnSettingsTab', defaultMessage: 'Column Settings' })}
            id="dataGridSettingsTab-columns"
          />
          <Tab label={intl.formatMessage({ id: 'dataGridRowSettingsTab', defaultMessage: 'Row Settings' })} id="dataGridSettingsTab-rows" />
        </Tabs>

        {/* ===== Column Settings Tab ===== */}
        {tabValue === 0 && (
          <>
            <DrawerContainer id="dataGridrowSettingsBox">
              <TableContainer component={Paper} id="dataGridSettingsTableContainer">
                <Table size="small" id="dataGridSettingsTable">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 3.7 }}>
                        <Typography variant="subtitle" id="dataGridSettingsColNameHeader">
                          <FormattedMessage id="dataGridColumnNameHeader" defaultMessage="Column Name" />
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle" id="dataGridSettingsShowColHeader">
                          <FormattedMessage id="dataGridShowColumnHeader" defaultMessage="Show Column" />
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle" id="dataGridSettingsLeftPinHeader">
                          <FormattedMessage id="dataGridLeftPinHeader" defaultMessage="Left Pin" />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Get columns in current order from tempColumnOrder
                      const orderedCols =
                        tempColumnOrder.length > 0
                          ? tempColumnOrder
                              .map((field) => columns.find((col) => col.field === field))
                              .filter(Boolean)
                              .filter((col) => col.field !== 'actions')
                              .filter((col) => !col.hide)
                          : columns.filter((col) => col.field !== 'actions').filter((col) => !col.hide);

                      return orderedCols.map((col) => (
                        <TableRow key={col.field} id={`dataGridSettingsRow-${col.field}`}>
                          <TableCell sx={{ pl: 3.7 }}>
                            <Typography variant="body1" id={`dataGridSettingsColName-${col.field}`}>
                              {getColumnDisplayLabel(col)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={!!tempColumnVisibility[col.field]}
                              onChange={() => handleToggleColumnTemp(col.field)}
                              size="small"
                              // hideable: false columns are always shown, so offering a
                              // toggle here would not survive the next load
                              disabled={col.hideable === false}
                              id={`dataGridSettingsShowColCheckbox-${col.field}`}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={tempPinnedColumns.includes(col.field)}
                              onChange={() => handleTogglePinTemp(col.field)}
                              size="small"
                              id={`dataGridSettingsLeftPinCheckbox-${col.field}`}
                            />
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </DrawerContainer>

            <DrawerFooter id="dataGridSettingsDrawerFooter">
              <Button variant="outlined" onClick={handleResetSettings} id="dataGridSettingsCancelBtn">
                <FormattedMessage id="reset" defaultMessage="Reset" />
              </Button>
              <Button variant="contained" color="primary" onClick={saveUserPreference} id="dataGridSettingsDoneBtn">
                <FormattedMessage id="apply" defaultMessage="Apply" />
              </Button>
            </DrawerFooter>
          </>
        )}

        {/* ===== Row Settings Tab ===== */}
        {tabValue === 1 && (
          <>
            <DrawerContainer id="dataGridrowSettingsBox">
              <Box sx={{ pl: 3.7, pr: 2, pt: 0.7 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }} id="dataGridRowDensityLabel">
                  <FormattedMessage id="dataGridRowDensity" defaultMessage="Row Density" />
                </Typography>
                <Box display="flex" mb={3} id="dataGridRowDensityRadioGroup">
                  <FormControlLabel
                    control={
                      <Radio
                        checked={tempDensity === 'compact'}
                        onChange={() => handleDensityTemp('compact')}
                        id="dataGridDensityRadio-compact"
                      />
                    }
                    label={<FormattedMessage id="dataGridDensityCompact" defaultMessage="Compact" />}
                    id="dataGridDensityLabel-compact"
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={tempDensity === 'standard'}
                        onChange={() => handleDensityTemp('standard')}
                        id="dataGridDensityRadio-standard"
                      />
                    }
                    label={<FormattedMessage id="dataGridDensityStandard" defaultMessage="Standard" />}
                    id="dataGridDensityLabel-standard"
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={tempDensity === 'comfortable'}
                        onChange={() => handleDensityTemp('comfortable')}
                        id="dataGridDensityRadio-comfortable"
                      />
                    }
                    label={<FormattedMessage id="dataGridDensityComfortable" defaultMessage="Comfortable" />}
                    id="dataGridDensityLabel-comfortable"
                  />
                </Box>

                {showRowActionSettings && (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 1 }} id="dataGridInRowActionLabel">
                      <FormattedMessage id="dataGridInRowAction" defaultMessage="In-Row Action" />
                    </Typography>
                    <Box>
                      <RadioGroup
                        name="actionColumnShow"
                        value={tempRowAction}
                        onChange={(e) => handleRowActionTemp(e.target.value)}
                        row
                        id="dataGridInRowActionRadioGroup"
                      >
                        <FormControlLabel
                          value="show"
                          control={<Radio id="dataGridInRowActionRadio-show" />}
                          label={<FormattedMessage id="dataGridShow" defaultMessage="Show" />}
                          id="dataGridInRowActionLabel-show"
                        />
                        <FormControlLabel
                          value="hide"
                          control={<Radio id="dataGridInRowActionRadio-hide" />}
                          label={<FormattedMessage id="dataGridHide" defaultMessage="Hide" />}
                          id="dataGridInRowActionLabel-hide"
                        />
                      </RadioGroup>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }} id="dataGridInRowActionCaption">
                      <FormattedMessage
                        id="dataGridInRowActionHelp"
                        defaultMessage="Displaying in-row actions can obscure content of the columns."
                      />
                    </Typography>
                  </>
                )}
              </Box>
            </DrawerContainer>

            <DrawerFooter id="dataGridRowSettingsFooter">
              <Button variant="outlined" onClick={handleResetSettings} id="dataGridRowSettingsCancelBtn">
                <FormattedMessage id="reset" defaultMessage="Reset" />
              </Button>
              <Button variant="contained" color="primary" onClick={saveUserPreference} id="dataGridRowSettingsDoneBtn">
                <FormattedMessage id="apply" defaultMessage="Apply" />
              </Button>
            </DrawerFooter>
          </>
        )}
      </CommonDrawer>
      {/* Filter Drawer */}
      <CommonDrawer
        open={filterDrawerOpen}
        onClose={() => toggleFilterDrawer(false)}
        hideBackdrop={false}
        ModalProps={{
          disableEscapeKeyDown: false,
          BackdropProps: { invisible: true },
          disableEnforceFocus: true,
          disableAutoFocus: true
        }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 20px)', md: '600px' }
          }
        }}
        id="dataGridFilterDrawer"
      >
        <DrawerHeader id="dataGridFilterDrawerHeader">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h3" id="dataGridFilterDrawerTitle">
              {callingFrom === 'notifications'
                ? intl.formatMessage({ id: 'filter', defaultMessage: 'Filter' })
                : intl.formatMessage({ id: 'dataGridDataFilterTitle', defaultMessage: 'Data Filter' })}
            </Typography>
            <IconButton
              onClick={toggleFilterDrawer}
              size="small"
              id="dataGridFilterDrawerCloseBtn"
              aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
            >
              <CloseIcon id="dataGridFilterDrawerCloseIcon" />
            </IconButton>
          </Box>
        </DrawerHeader>
        <DrawerContainer id="dataGridFilterDrawerBody">
          {customFilterHandlers?.renderCustomFilters ? (
            customFilterHandlers.renderCustomFilters(customFilterData, handleCustomFilterSelect)
          ) : (
            <Box sx={{ pl: 3.7, pr: 0.5, pt: 3 }} id="dataGridFilterDrawerBody">
              {draftFilters.map((filter, index) => {
                const columnForFilter = columns.find((col) => col.filterColumnName === filter.field);
                const filterOptions = columnForFilter?.filtersValue ?? filter.filtersValue;
                return (
                  <Box key={index} sx={{ mb: 3 }} id={`dataGridFilterRow-${index}`}>
                    <Box display="flex" alignItems="end" gap={2}>
                      <FormControl
                        className="inputBackTransparent"
                        size="small"
                        sx={{ width: '220px' }}
                        id={`dataGridFilterColSelectBox-${index}`}
                      >
                        <Typography
                          variant="body1"
                          sx={{ mb: 1, alignItems: 'center', display: 'flex' }}
                          id={`dataGridFilterColLabel-${index}`}
                        >
                          <FormattedMessage id="dataGridFilterColumnsLabel" defaultMessage="Columns" />
                        </Typography>
                        <Select
                          value={filter.field || columns[index]?.filterColumnName || ''}
                          onChange={(e) => handleFilterValueChange(index, 'field', e.target.value)}
                          displayEmpty
                          IconComponent={ExpandMoreIcon}
                          id={`dataGridFilterColSelect-${index}`}
                          data-field-label={intl.formatMessage({ id: 'dataGridFilterColumnsLabel', defaultMessage: 'Columns' })}
                          data-type="select"
                        >
                          {columns.map((col) => {
                            const isFilterable = !col.hasOwnProperty('filterable') || col.filterable != false;
                            const colName = col.filterColumnName ?? col.field;
                            const usedByOtherRows = draftFilters.some((f, i) => i !== index && f.field && colName && f.field === colName);
                            const isCurrentSelection = filter.field === colName;
                            if (!isFilterable) return null;
                            if (usedByOtherRows && !isCurrentSelection) return null;
                            return (
                              <MenuItem key={col.field} value={colName} id={`dataGridFilterColOption-${col.field}-${index}`}>
                                {getColumnDisplayLabel(col)}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      {/* As per Manish Gambhir feedback */}
                      <Box sx={{ mb: 1.2 }}>=</Box>
                      <FormControl size="small" sx={{ width: '220px' }} id={`dataGridFilterValueBox-${index}`}>
                        <Box sx={{ mb: 1, alignItems: 'center', display: 'flex' }}>
                          <Typography variant="body1" id={`dataGridFilterValueLabel-${index}`}>
                            <FormattedMessage id="dataGridFilterValueLabel" defaultMessage="Value" />
                          </Typography>
                        </Box>
                        {Array.isArray(filterOptions) && filterOptions.length > 0 ? (
                          <Select
                            value={filter.value || ''}
                            onChange={(e) => handleFilterValueChange(index, 'value', e.target.value)}
                            displayEmpty
                            id={`dataGridfilterValueSelect-${index}`}
                            data-field-label={intl.formatMessage({ id: 'dataGridFilterValueLabel', defaultMessage: 'Value' })}
                            data-type="select"
                          >
                            {(() => {
                              const fv = filterOptions;
                              const isDictArray = fv.length === 1 && fv[0] && typeof fv[0] === 'object' && !Array.isArray(fv[0]);
                              if (isDictArray) {
                                const dict = fv[0];
                                return Object.keys(dict).map((key) => (
                                  <MenuItem
                                    key={`filter-value-${index}-${key}`}
                                    value={key}
                                    id={`dataGridFilterValueOption-${index}-${key}`}
                                  >
                                    {String(dict[key])}
                                  </MenuItem>
                                ));
                              }
                              return fv.map((item, optIdx) => (
                                <MenuItem
                                  key={`filter-value-${index}-${optIdx}`}
                                  value={item}
                                  id={`dataGridFilterValueOption-${index}-${optIdx}`}
                                >
                                  {String(item)}
                                </MenuItem>
                              ));
                            })()}
                          </Select>
                        ) : (
                          <TextField
                            value={filter.value}
                            className="inputBackTransparent"
                            size="small"
                            placeholder={intl.formatMessage({ id: 'dataGridFilterValueLabel', defaultMessage: 'Value' })}
                            onChange={(e) => handleFilterValueChange(index, 'value', e.target.value)}
                            id={`dataGridFilterValueInput-${index}`}
                            data-field-label={intl.formatMessage({ id: 'dataGridFilterValueLabel', defaultMessage: 'Value' })}
                            data-type="text"
                          />
                        )}
                      </FormControl>

                      <Box sx={{ mb: 0.7, display: 'flex' }} id={`dataGridFilterRowBtnBox-${index}`}>
                        {/* Add button (always rendered) */}
                        <IconButton
                          size="small"
                          onClick={addFilterRow}
                          id={`dataGridAddFilterRowBtn-${index}`}
                          aria-label={intl.formatMessage({ id: 'dataGridAddFilterRow', defaultMessage: 'Add filter row' })}
                          sx={{
                            visibility: index === draftFilters.length - 1 ? 'visible' : 'hidden'
                          }}
                        >
                          <AddIcon color="primary" fontSize="small" />
                        </IconButton>

                        {/* Show remove icon on all rows EXCEPT when only 1 row exists */}
                        {draftFilters.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => removeFilterRow(index)}
                            id={`dataGridRemoveFilterRowBtn-${index}`}
                            aria-label={intl.formatMessage({ id: 'dataGridRemoveFilterRow', defaultMessage: 'Remove filter row' })}
                          >
                            <CloseIcon color="error" fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DrawerContainer>
        <DrawerFooter sx={{ justifyContent: 'space-between' }} id="dataGridFilterDrawerFooter">
          <Box sx={{ gap: '10px 19px', display: 'flex' }}>
            <Button
              variant="outlined"
              onClick={toggleFilterDrawer}
              id="dataGridFilterCancelBtn"
              data-field-label={intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
              data-type="button"
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={customFilterHandlers?.clearFilters ? clearCustomFilters : removeAllFilters}
              id="dataGridFilterRemoveAllBtn"
              data-field-label={intl.formatMessage({ id: 'clearAll', defaultMessage: 'Clear All' })}
              data-type="button"
            >
              <FormattedMessage id="clearAll" defaultMessage="Clear All" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={applyFilters}
              id="dataGridFilterApplyBtn"
              data-field-label={intl.formatMessage({ id: 'apply', defaultMessage: 'Apply' })}
              data-type="button"
            >
              <FormattedMessage id="apply" defaultMessage="Apply" />
            </Button>
          </Box>
        </DrawerFooter>
      </CommonDrawer>
      <Snackbar open={userPrefSuccessOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} id="dataGridSnackbar">
        <Alert severity="success" variant="filled" sx={{ width: '100%' }} id="dataGridSnackbarAlert">
          {intl.formatMessage({ id: 'dataGridUserPreferencesSaved', defaultMessage: 'User preferences saved successfully.' })}
        </Alert>
      </Snackbar>
      <Snackbar
        open={pinLimitAlertOpen}
        autoHideDuration={3000}
        onClose={() => setPinLimitAlertOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        id="dataGridPinLimitSnackbar"
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setPinLimitAlertOpen(false)}
          sx={{ width: '100%' }}
          id="dataGridPinLimitSnackbarAlert"
        >
          {intl.formatMessage({
            id: 'dataGridPinnedItemsLimitReached',
            defaultMessage: 'Limit of pinned items has reached. You need to unpinned some items if you want to pinned another item.'
          })}
        </Alert>
      </Snackbar>
      <PageMetaHiddenField meta={PageMeta} pageName="Page" data-testid="page-meta-object" hidden /></Box>)
  );
};

export default SmartDataGrid;

export const PageMeta = {
  pageName: "Smart Data Grid",

  pageObjects: [{
    field_name: "dataGridSearchInput",
    field_id: "dataGridSearchInput",
    type: "text",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "Unknown+Data Grid Filter Value Input ",
    field_id: "dataGridFilterValueInput-",
    type: "text",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridRowsPerPageSelect",
    field_id: "dataGridRowsPerPageSelect",
    type: "select",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridFilterColumnsLabel",
    field_id: "dataGridFilterColSelect-",
    type: "select",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false,
    hasDynamicOptions: true,
    note: "This field has additional options loaded dynamically at runtime (e.g., from API calls). The options listed above are only the static options available at build time."
  }, {
    field_name: "Unknown+Data Gridfilter Value Select ",
    field_id: "dataGridfilterValueSelect-",
    type: "select",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridSettingsCancelBtn",
    field_id: "dataGridSettingsCancelBtn",
    type: "button",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridSettingsDoneBtn",
    field_id: "dataGridSettingsDoneBtn",
    type: "button",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridRowSettingsCancelBtn",
    field_id: "dataGridRowSettingsCancelBtn",
    type: "button",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridRowSettingsDoneBtn",
    field_id: "dataGridRowSettingsDoneBtn",
    type: "button",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridFilterCancelBtn",
    field_id: "dataGridFilterCancelBtn",
    type: "button",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridFilterRemoveAllBtn",
    field_id: "dataGridFilterRemoveAllBtn",
    type: "button",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridFilterApplyBtn",
    field_id: "dataGridFilterApplyBtn",
    type: "button",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "Unknown+Data Grid Settings Show Col Checkbox ",
    field_id: "dataGridSettingsShowColCheckbox-",
    type: "checkBox",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "Unknown+Data Grid Settings Left Pin Checkbox ",
    field_id: "dataGridSettingsLeftPinCheckbox-",
    type: "checkBox",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridDensityRadio-compact",
    field_id: "dataGridDensityRadio-compact",
    type: "radioButton",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridDensityRadio-standard",
    field_id: "dataGridDensityRadio-standard",
    type: "radioButton",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridDensityRadio-comfortable",
    field_id: "dataGridDensityRadio-comfortable",
    type: "radioButton",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false
  }, {
    field_name: "dataGridInRowActionRadioGroup",
    field_id: "dataGridInRowActionRadioGroup",
    type: "radioGroup",
    minLength: null,
    maxLength: null,
    allowed_regex: null,
    isMandatory: false,

    options: [{
      value: "show",
      label: "dataGridInRowActionRadio-show"
    }, {
      value: "hide",
      label: "dataGridInRowActionRadio-hide"
    }]
  }]
};
